import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Plus, Save, Trash2 } from 'lucide-react';
import { fmtDateTimeLocal, parseDateTimeLocal, Modal, SectionCard } from '../Scheduler/utils';
import eventsData from '../../eventsData.json';
import { useEntitlements } from '../../context/EntitlementsContext';
import { useNudge } from '../../context/NudgeContext';
import LiveMusicLibraryPanel from './LiveMusicLibraryPanel';
import KaraokeRuntimePanel from './KaraokeRuntimePanel';
import {
  buildDefaultEndCondition,
  buildDefaultProgrammeBranding,
  normalizeEndCondition,
  normalizeProgrammeBranding,
  normalizeSearchSuffix,
} from './liveExperienceProgrammeConfig';
import './Events.css';

const schedulerFetch = async (path, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const normalizedPath = path.startsWith('/api/') ? path.slice(4) : path;
  const res = await fetch(`/api/scheduler${normalizedPath}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const txt = await res.text();
    throw new Error(txt || 'Scheduler request failed');
  }
  return res.json();
};

const appFetch = async (path, options = {}) => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const normalizedPath = path.startsWith('/api/') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  const res = await fetch(normalizedPath, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let message = 'Request failed';
    try {
      const payload = await res.json();
      message = payload?.message || payload?.error || message;
    } catch (_error) {
      const text = await res.text();
      message = text || message;
    }
    throw new Error(message);
  }
  return res.json();
};

const buildNyeTarget = () => {
  const now = new Date();
  const year = now.getFullYear();
  return new Date(year, 11, 31, 23, 59, 59);
};

const isCountdownTemplate = (template) =>
  String(template?.editor_key || '').trim().toLowerCase() === 'countdown'
  || String(template?.name || '').trim().toLowerCase().includes('countdown');

const isKaraokeTemplate = (template) =>
  String(template?.editor_key || '').trim().toLowerCase() === 'karaoke'
  || String(template?.name || '').trim().toLowerCase().includes('karaoke');

const isJukeboxTemplate = (template) =>
  String(template?.editor_key || '').trim().toLowerCase() === 'jukebox'
  || String(template?.name || '').trim().toLowerCase().includes('jukebox');

const isDedicatedSongQueueTemplate = (template) =>
  isKaraokeTemplate(template) || isJukeboxTemplate(template);

const isTemplateAvailable = (template) =>
  String(template?.status || 'live').trim().toLowerCase() === 'live';

const getTemplateHighlights = (template) => (
  Array.isArray(template?.highlights) ? template.highlights.filter(Boolean) : []
);

const KARAOKE_DEFAULT_REQUESTS_PER_GUEST = 3;
const KARAOKE_DEFAULT_VOTES_PER_GUEST = 1;
const JUKEBOX_DEFAULT_REQUESTS_PER_GUEST = 6;
const JUKEBOX_DEFAULT_VOTES_PER_GUEST = 3;
const DEFAULT_GUEST_ACCESS_HOURS = 4;

const buildDefaultKaraokeConfig = () => ({
  plugin_key: 'karaoke',
  experience_key: 'karaoke-night',
  programme_branding: buildDefaultProgrammeBranding(),
  end_condition: buildDefaultEndCondition(),
  approval_required: false,
  allow_voting: false,
  filler_mode: 'ambient_video',
  karaoke_search_suffix: 'karaoke',
  max_search_results: 8,
  max_requests_per_guest: KARAOKE_DEFAULT_REQUESTS_PER_GUEST,
  max_votes_per_guest: KARAOKE_DEFAULT_VOTES_PER_GUEST,
  guest_access_hours: DEFAULT_GUEST_ACCESS_HOURS,
  promotion_qr_size: 22,
  choice_playlist_mode: 'off',
  choice_playlist_name: '',
  performer_screen_device_id: null,
  mirror_playback_device_ids: [],
  promotion_screen_device_ids: [],
  audience_display_device_ids: [],
  live_session: null,
});

const buildDefaultJukeboxConfig = () => ({
  plugin_key: 'jukebox',
  experience_key: 'jukebox-night',
  programme_branding: buildDefaultProgrammeBranding(),
  end_condition: buildDefaultEndCondition(),
  approval_required: false,
  allow_voting: false,
  search_suffix: 'music video',
  max_search_results: 8,
  max_song_duration_minutes: 0,
  youtube_short_video_filter: false,
  max_requests_per_guest: JUKEBOX_DEFAULT_REQUESTS_PER_GUEST,
  max_votes_per_guest: JUKEBOX_DEFAULT_VOTES_PER_GUEST,
  guest_access_hours: DEFAULT_GUEST_ACCESS_HOURS,
  promotion_qr_size: 22,
  overlay_transparency: 20,
  playback_target_device_id: null,
  choice_playlist_mode: 'off',
  choice_playlist_name: '',
  qr_overlay_targets: [],
  live_session: null,
});

const normalizePositiveIntegerList = (value) => {
  const items = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : value == null
        ? []
        : [value];

  return Array.from(
    new Set(
      items
        .map((entry) => Number(entry))
        .filter((entry) => Number.isInteger(entry) && entry > 0)
    )
  );
};

const normalizeQrOverlayTargets = (value, legacyFullTargets = []) => {
  const allowedPositions = new Set([
    'top',
    'bottom',
    'full',
    'full_permanent',
    'full_on_queue_addition',
  ]);
  const fromStructured = Array.isArray(value)
    ? value
        .map((entry) => {
          const deviceId = Number(entry?.device_id || entry?.deviceId || entry?.id || 0);
          const position = String(entry?.position || entry?.overlay_position || entry?.overlayPosition || 'full')
            .trim()
            .toLowerCase();
          if (!Number.isInteger(deviceId) || deviceId <= 0 || !allowedPositions.has(position)) return null;
          const duration = Number(entry?.duration_sec || entry?.durationSec || entry?.duration || 0);
          const textSizePx = Number(entry?.text_size_px ?? entry?.textSizePx ?? 24);
          const qrSizePx = Number(entry?.qr_size_px ?? entry?.qrSizePx ?? 140);
          const widthPercent = Number(entry?.width_percent ?? entry?.widthPercent ?? 80);
          const transparency = Number(entry?.transparency ?? entry?.overlay_transparency ?? 80);
          return {
            device_id: deviceId,
            position,
            duration_sec: Number.isFinite(duration) && duration > 0 ? duration : null,
            text_size_px: Math.min(64, Math.max(12, Number.isFinite(textSizePx) ? textSizePx : 24)),
            qr_size_px: Math.min(320, Math.max(64, Number.isFinite(qrSizePx) ? qrSizePx : 140)),
            width_percent: Math.min(100, Math.max(40, Number.isFinite(widthPercent) ? widthPercent : 80)),
            transparency: Math.min(95, Math.max(0, Number.isFinite(transparency) ? transparency : 80)),
          };
        })
        .filter(Boolean)
    : [];

  const byDevice = new Map();
  normalizePositiveIntegerList(legacyFullTargets).forEach((deviceId) => {
    byDevice.set(deviceId, {
      device_id: deviceId,
      position: 'full',
      duration_sec: null,
      text_size_px: 24,
      qr_size_px: 140,
      width_percent: 80,
      transparency: 80,
    });
  });
  let hasQueueActivityTarget = false;
  fromStructured.forEach((target) => {
    if (target.position === 'full_on_queue_addition') {
      if (hasQueueActivityTarget) return;
      hasQueueActivityTarget = true;
    }
    byDevice.set(target.device_id, target);
  });
  return Array.from(byDevice.values());
};

const normalizeKaraokeConfig = (config) => {
  const raw = config && typeof config === 'object' ? config : {};
  const liveSession = raw.live_session || raw.liveSession || null;
  const promotionScreenDeviceIds = normalizePositiveIntegerList(
    raw.promotion_screen_device_ids
    ?? raw.promotionScreenDeviceIds
    ?? raw.audience_display_device_ids
    ?? raw.audienceDisplayDeviceIds
  );
  return {
    ...buildDefaultKaraokeConfig(),
    ...raw,
    programme_branding: normalizeProgrammeBranding(
      raw.programme_branding ?? raw.programmeBranding
    ),
    end_condition: normalizeEndCondition(raw.end_condition ?? raw.endCondition),
    approval_required: !!(raw.approval_required ?? raw.approvalRequired ?? false),
    allow_voting: !!(raw.allow_voting ?? raw.allowVoting ?? false),
    filler_mode: String(raw.filler_mode || raw.fillerMode || 'ambient_video') === 'silence'
      ? 'silence'
      : 'ambient_video',
    karaoke_search_suffix: normalizeSearchSuffix(
      raw.karaoke_search_suffix ?? raw.karaokeSearchSuffix,
      'karaoke'
    ),
    max_search_results: Math.min(
      10,
      Math.max(3, Number(raw.max_search_results || raw.maxSearchResults || 8) || 8)
    ),
    max_requests_per_guest: Math.min(
      20,
      Math.max(
        1,
        Number(
          raw.max_requests_per_guest
          ?? raw.maxRequestsPerGuest
          ?? KARAOKE_DEFAULT_REQUESTS_PER_GUEST
        ) || KARAOKE_DEFAULT_REQUESTS_PER_GUEST
      )
    ),
    max_votes_per_guest: Math.min(
      100,
      Math.max(
        1,
        Number(
          raw.max_votes_per_guest
          ?? raw.maxVotesPerGuest
          ?? KARAOKE_DEFAULT_VOTES_PER_GUEST
        ) || KARAOKE_DEFAULT_VOTES_PER_GUEST
      )
    ),
    guest_access_hours: Math.min(
      24,
      Math.max(
        1,
        Number(raw.guest_access_hours ?? raw.guestAccessHours ?? DEFAULT_GUEST_ACCESS_HOURS)
          || DEFAULT_GUEST_ACCESS_HOURS
      )
    ),
    promotion_qr_size: Math.min(
      80,
      Math.max(10, Number(raw.promotion_qr_size || raw.promotionQrSize || 22) || 22)
    ),
    choice_playlist_mode: String(
      raw.choice_playlist_mode || raw.choicePlaylistMode || 'off'
    ).trim().toLowerCase() === 'new_playlist' ? 'new_playlist' : 'off',
    choice_playlist_name: String(raw.choice_playlist_name || raw.choicePlaylistName || '').trim(),
    performer_screen_device_id: Number(
      raw.performer_screen_device_id || raw.performerScreenDeviceId || 0
    ) || null,
    mirror_playback_device_ids: normalizePositiveIntegerList(
      raw.mirror_playback_device_ids ?? raw.mirrorPlaybackDeviceIds
    ),
    promotion_screen_device_ids: promotionScreenDeviceIds,
    audience_display_device_ids: normalizePositiveIntegerList(
      raw.audience_display_device_ids ?? raw.audienceDisplayDeviceIds
    ),
    qr_overlay_targets: normalizeQrOverlayTargets(
      raw.qr_overlay_targets ?? raw.qrOverlayTargets,
      promotionScreenDeviceIds
    ),
    live_session: liveSession && typeof liveSession === 'object'
      ? {
          session_id: Number(liveSession.session_id || liveSession.sessionId) || null,
          public_key: String(liveSession.public_key || liveSession.publicKey || '').trim() || null,
          status: String(liveSession.status || '').trim() || null,
          launched_at: liveSession.launched_at || liveSession.launchedAt || null,
          guest_path: String(liveSession.guest_path || liveSession.guestPath || '').trim() || null,
        }
      : null,
  };
};

const normalizeJukeboxConfig = (config) => {
  const raw = config && typeof config === 'object' ? config : {};
  const liveSession = raw.live_session || raw.liveSession || null;
  const fullOverlayMode = String(raw.full_overlay_mode || raw.fullOverlayMode || 'on_queue_addition')
    .trim()
    .toLowerCase();
  const legacyTargets = normalizePositiveIntegerList(
    raw.promotion_screen_device_ids
    ?? raw.promotionScreenDeviceIds
    ?? raw.audience_display_device_ids
    ?? raw.audienceDisplayDeviceIds
  );
  const qrOverlayTargets = normalizeQrOverlayTargets(
    raw.qr_overlay_targets ?? raw.qrOverlayTargets,
    legacyTargets
  ).map((target) => {
    if (target.position !== 'full') return target;
    return {
      ...target,
      position: fullOverlayMode === 'permanent'
        ? 'full_permanent'
        : 'full_on_queue_addition',
      duration_sec: target.duration_sec || (fullOverlayMode === 'permanent' ? null : 30),
    };
  });
  return {
    ...buildDefaultJukeboxConfig(),
    ...raw,
    programme_branding: normalizeProgrammeBranding(
      raw.programme_branding ?? raw.programmeBranding
    ),
    end_condition: normalizeEndCondition(raw.end_condition ?? raw.endCondition),
    approval_required: !!(raw.approval_required ?? raw.approvalRequired ?? false),
    allow_voting: !!(raw.allow_voting ?? raw.allowVoting ?? false),
    search_suffix: normalizeSearchSuffix(
      raw.search_suffix ?? raw.searchSuffix,
      'music video'
    ),
    max_search_results: Math.min(
      10,
      Math.max(3, Number(raw.max_search_results || raw.maxSearchResults || 8) || 8)
    ),
    max_song_duration_minutes: Math.min(
      60,
      Math.max(0, Number(raw.max_song_duration_minutes ?? raw.maxSongDurationMinutes ?? 0) || 0)
    ),
    youtube_short_video_filter: !!(
      raw.youtube_short_video_filter ?? raw.youtubeShortVideoFilter ?? false
    ),
    max_requests_per_guest: Math.min(
      20,
      Math.max(
        1,
        Number(
          raw.max_requests_per_guest
          ?? raw.maxRequestsPerGuest
          ?? JUKEBOX_DEFAULT_REQUESTS_PER_GUEST
        ) || JUKEBOX_DEFAULT_REQUESTS_PER_GUEST
      )
    ),
    max_votes_per_guest: Math.min(
      100,
      Math.max(
        1,
        Number(
          raw.max_votes_per_guest
          ?? raw.maxVotesPerGuest
          ?? JUKEBOX_DEFAULT_VOTES_PER_GUEST
        ) || JUKEBOX_DEFAULT_VOTES_PER_GUEST
      )
    ),
    guest_access_hours: Math.min(
      24,
      Math.max(
        1,
        Number(raw.guest_access_hours ?? raw.guestAccessHours ?? DEFAULT_GUEST_ACCESS_HOURS)
          || DEFAULT_GUEST_ACCESS_HOURS
      )
    ),
    promotion_qr_size: Math.min(
      80,
      Math.max(10, Number(raw.promotion_qr_size || raw.promotionQrSize || 22) || 22)
    ),
    overlay_transparency: Math.min(
      90,
      Math.max(0, Number(raw.overlay_transparency ?? raw.overlayTransparency ?? 20) || 0)
    ),
    choice_playlist_mode: ['current_fallback', 'new_playlist'].includes(
      String(raw.choice_playlist_mode || raw.choicePlaylistMode || 'off').trim().toLowerCase()
    )
      ? String(raw.choice_playlist_mode || raw.choicePlaylistMode).trim().toLowerCase()
      : 'off',
    choice_playlist_name: String(raw.choice_playlist_name || raw.choicePlaylistName || '').trim(),
    playback_target_device_id: Number(
      raw.playback_target_device_id || raw.playbackTargetDeviceId || raw.performer_screen_device_id || 0
    ) || null,
    full_overlay_mode: fullOverlayMode === 'permanent'
      ? 'permanent'
      : 'on_queue_addition',
    qr_overlay_targets: qrOverlayTargets,
    live_session: liveSession && typeof liveSession === 'object'
      ? {
          session_id: Number(liveSession.session_id || liveSession.sessionId) || null,
          public_key: String(liveSession.public_key || liveSession.publicKey || '').trim() || null,
          status: String(liveSession.status || '').trim() || null,
          launched_at: liveSession.launched_at || liveSession.launchedAt || null,
          guest_path: String(liveSession.guest_path || liveSession.guestPath || '').trim() || null,
        }
      : null,
  };
};

const getKaraokeGuestPath = (config) => {
  const karaokeConfig = normalizeKaraokeConfig(config);
  const publicKey = karaokeConfig.live_session?.public_key;
  return publicKey ? `/live/${publicKey}` : null;
};

const getJukeboxGuestPath = (config) => {
  const jukeboxConfig = normalizeJukeboxConfig(config);
  const publicKey = jukeboxConfig.live_session?.public_key;
  return publicKey ? `/live/${publicKey}` : null;
};

const getSongQueueDeviceIds = (template, config) => {
  if (isJukeboxTemplate(template)) {
    const jukeboxConfig = normalizeJukeboxConfig(config);
    return normalizePositiveIntegerList([
      jukeboxConfig.playback_target_device_id,
      ...jukeboxConfig.qr_overlay_targets.map((target) => target.device_id),
    ]);
  }
  if (isKaraokeTemplate(template)) {
    const karaokeConfig = normalizeKaraokeConfig(config);
    return normalizePositiveIntegerList([
      karaokeConfig.performer_screen_device_id,
      ...karaokeConfig.qr_overlay_targets.map((target) => target.device_id),
      ...karaokeConfig.promotion_screen_device_ids,
    ]);
  }
  return [];
};

const formatKaraokeDeviceLabel = (device) => {
  if (!device) return 'Unknown device';
  const areaName = String(device.areaName || '').trim();
  return areaName ? `${device.name} • ${areaName}` : device.name;
};

export default function Events({
  siteId,
  deviceId,
  openEventInstanceId,
  onConsumeOpenRequest,
  mode = 'settings',
}) {
  const karaokeDashboardMode = mode === 'karaoke-dashboard';
  const jukeboxDashboardMode = mode === 'jukebox-dashboard';
  const dedicatedSongQueueDashboardMode = karaokeDashboardMode || jukeboxDashboardMode;
  const selectedDashboardDeviceId = Number(deviceId) || null;
  const { entitlements } = useEntitlements();
  const { showNudge } = useNudge();
  const stageMode = entitlements?.stage_mode || 'demo';
  const stageLocked = stageMode === 'demo';

  const [templates, setTemplates] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Plugins');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [previewTick, setPreviewTick] = useState(0);
  const [comingSoonCategory, setComingSoonCategory] = useState('Plugins');
  const [selectedComingSoon, setSelectedComingSoon] = useState(null);
  const [savedAssets, setSavedAssets] = useState([]);
  const [savedAssetsLoading, setSavedAssetsLoading] = useState(false);
  const [savedAssetSearch, setSavedAssetSearch] = useState('');
  const [runtimeModalOpen, setRuntimeModalOpen] = useState(false);
  const [runtimeInstanceId, setRuntimeInstanceId] = useState(null);
  const [runtimeData, setRuntimeData] = useState(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeActing, setRuntimeActing] = useState(false);
  const [karaokeDeviceTargets, setKaraokeDeviceTargets] = useState([]);
  const [karaokeDeviceTargetsLoading, setKaraokeDeviceTargetsLoading] = useState(false);

  const countdownTemplate = useMemo(() => {
    const byEditorKey = templates.find((t) => isCountdownTemplate(t));
    return byEditorKey || templates[0] || null;
  }, [templates]);
  const karaokeTemplate = useMemo(
    () => templates.find((template) => isKaraokeTemplate(template)) || null,
    [templates]
  );
  const jukeboxTemplate = useMemo(
    () => templates.find((template) => isJukeboxTemplate(template)) || null,
    [templates]
  );
  const activeSongQueueDefinition = useMemo(() => {
    if (karaokeDashboardMode) {
      return {
        key: 'karaoke',
        label: 'Karaoke',
        apiBase: '/karaoke',
        normalizeConfig: normalizeKaraokeConfig,
        getGuestPath: getKaraokeGuestPath,
      };
    }
    if (jukeboxDashboardMode) {
      return {
        key: 'jukebox',
        label: 'Jukebox',
        apiBase: '/jukebox',
        normalizeConfig: normalizeJukeboxConfig,
        getGuestPath: getJukeboxGuestPath,
      };
    }
    return null;
  }, [jukeboxDashboardMode, karaokeDashboardMode]);
  const templateById = useMemo(
    () => new Map(templates.map((template) => [Number(template.id), template])),
    [templates]
  );
  const editingTemplate = useMemo(
    () => templateById.get(Number(editing?.event_template_id || 0)) || null,
    [editing?.event_template_id, templateById]
  );
  const editingTemplateIsCountdown = isCountdownTemplate(editingTemplate || countdownTemplate);
  const editingTemplateIsKaraoke = isKaraokeTemplate(editingTemplate);
  const editingTemplateIsJukebox = isJukeboxTemplate(editingTemplate);
  const songQueueAreaIdByDeviceId = useMemo(() => new Map(
    karaokeDeviceTargets.map((target) => [Number(target.id), Number(target.areaId) || null])
  ), [karaokeDeviceTargets]);
  const songQueueTimeZoneByDeviceId = useMemo(() => new Map(
    karaokeDeviceTargets.map((target) => [Number(target.id), target.timeZone || 'UTC'])
  ), [karaokeDeviceTargets]);
  const selectedDashboardAreaId = songQueueAreaIdByDeviceId.get(selectedDashboardDeviceId) || null;
  const editingPrimaryPlaybackDeviceId = Number(
    editingTemplateIsJukebox
      ? editing?.config?.playback_target_device_id
      : editing?.config?.performer_screen_device_id
  ) || null;
  const editingPrimaryAreaId = songQueueAreaIdByDeviceId.get(editingPrimaryPlaybackDeviceId) || null;
  const songQueueAreaTargets = useMemo(() => (
    editingPrimaryAreaId
      ? karaokeDeviceTargets.filter((target) => Number(target.areaId) === editingPrimaryAreaId)
      : []
  ), [editingPrimaryAreaId, karaokeDeviceTargets]);
  const songQueuePlaybackTargets = useMemo(() => (
    dedicatedSongQueueDashboardMode && selectedDashboardAreaId
      ? karaokeDeviceTargets.filter((target) => Number(target.areaId) === selectedDashboardAreaId)
      : karaokeDeviceTargets
  ), [dedicatedSongQueueDashboardMode, karaokeDeviceTargets, selectedDashboardAreaId]);

  const categories = useMemo(() => {
    const set = new Set();
    templates.forEach((t) => set.add(t.category || 'Plugins'));
    const list = Array.from(set);
    if (!list.includes('Plugins')) list.unshift('Plugins');
    return list;
  }, [templates]);

  const templatesInCategory = useMemo(() => {
    const cat = selectedCategory || 'Plugins';
    return templates
      .filter((t) => (t.category || 'Plugins') === cat)
      .filter((template) => {
        if (karaokeDashboardMode) return isKaraokeTemplate(template);
        if (jukeboxDashboardMode) return isJukeboxTemplate(template);
        return !isDedicatedSongQueueTemplate(template);
      });
  }, [jukeboxDashboardMode, karaokeDashboardMode, templates, selectedCategory]);

  const comingSoonCategories = useMemo(() => Object.keys(eventsData || {}), []);
  const comingSoonItems = useMemo(() => {
    const list = eventsData?.[comingSoonCategory] || [];
    return Array.isArray(list) ? list : [];
  }, [comingSoonCategory]);

  useEffect(() => {
    if (comingSoonCategories.length && !comingSoonCategories.includes(comingSoonCategory)) {
      setComingSoonCategory(comingSoonCategories[0]);
    }
  }, [comingSoonCategories, comingSoonCategory]);

  useEffect(() => {
    if (!comingSoonItems.length) {
      setSelectedComingSoon(null);
      return;
    }
    setSelectedComingSoon((prev) => {
      const stillExists = prev && comingSoonItems.find((item) => item.id === prev.id);
      return stillExists || comingSoonItems[0];
    });
  }, [comingSoonItems]);

  const fetchTemplates = useCallback(async () => {
    if (!siteId) return;
    try {
      const res = await schedulerFetch(`/api/event-templates?siteId=${encodeURIComponent(siteId)}`);
      setTemplates(Array.isArray(res.items) ? res.items : []);
    } catch (err) {
      console.error('[Events] Failed to fetch templates', err);
    }
  }, [siteId]);

  const fetchInstances = useCallback(async () => {
    if (!siteId) return;
    try {
      setLoading(true);
      const res = await schedulerFetch(`/api/event-instances?siteId=${encodeURIComponent(siteId)}`);
      setInstances(Array.isArray(res.items) ? res.items : []);
    } catch (err) {
      console.error('[Events] Failed to fetch instances', err);
      setMessage(err.message || 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  const loadKaraokeDeviceTargets = useCallback(async () => {
    if (!siteId) {
      setKaraokeDeviceTargets([]);
      return;
    }

    try {
      setKaraokeDeviceTargetsLoading(true);
      const areaResponse = await appFetch(`/sites/${siteId}/areas`);
      const areas = Array.isArray(areaResponse?.areas) ? areaResponse.areas : [];
      const deviceResponses = await Promise.all(
        areas.map(async (area) => {
          const result = await appFetch(`/areas/${area.id}/devices`);
          return {
            area,
            devices: Array.isArray(result?.devices) ? result.devices : [],
          };
        })
      );

      const nextTargets = deviceResponses
        .flatMap(({ area, devices }) =>
          devices.map((device) => ({
            id: Number(device.id),
            name: device.name || `Device #${device.id}`,
            type: String(device.type || '').trim(),
            areaName: area?.name || 'Area',
            areaId: Number(area?.id || 0) || null,
            timeZone: device.time_zone || device.timeZone || 'UTC',
          }))
        )
        .filter((device) => (
          Number.isInteger(device.id)
          && device.id > 0
          && ['TV', 'Music Player'].includes(device.type)
        ))
        .sort((a, b) => {
          const areaCompare = String(a.areaName || '').localeCompare(String(b.areaName || ''));
          if (areaCompare !== 0) return areaCompare;
          return String(a.name || '').localeCompare(String(b.name || ''));
        });

      setKaraokeDeviceTargets(nextTargets);
    } catch (err) {
      console.error('[Events] Failed to load karaoke device targets', err);
      setMessage(err.message || 'Failed to load device targets');
      setKaraokeDeviceTargets([]);
    } finally {
      setKaraokeDeviceTargetsLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  useEffect(() => {
    if (!siteId) return;
    let mounted = true;
    const controller = new AbortController();

    (async () => {
      try {
        setSavedAssetsLoading(true);
        const token = localStorage.getItem('token');
        const query = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
        const res = await fetch(`/api/creative/assets/saved${query}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error('Failed to fetch saved assets');
        const data = await res.json();
        if (!mounted) return;
        setSavedAssets(Array.isArray(data.assets) ? data.assets : []);
      } catch (err) {
        if (err?.name === 'AbortError') return;
        console.error('[Events] Failed to fetch saved assets', err);
      } finally {
        if (mounted) setSavedAssetsLoading(false);
      }
    })();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [siteId]);

  useEffect(() => {
    const editingSongQueue = modalOpen && (editingTemplateIsKaraoke || editingTemplateIsJukebox);
    if (!dedicatedSongQueueDashboardMode && !editingSongQueue) return;
    loadKaraokeDeviceTargets();
  }, [
    dedicatedSongQueueDashboardMode,
    editingTemplateIsJukebox,
    editingTemplateIsKaraoke,
    loadKaraokeDeviceTargets,
    modalOpen,
  ]);

  const openNew = (template = null) => {
    if (stageLocked) {
      showNudge({
        title: 'Upgrade to unlock Plugins',
        message: 'Plugins are in demo mode on your current plan. Upgrade to create and schedule plugin instances.',
      });
      return;
    }
    const chosen = template || (
      karaokeDashboardMode
        ? karaokeTemplate
        : jukeboxDashboardMode
          ? jukeboxTemplate
          : countdownTemplate
    );
    if (!chosen) {
      setMessage('No plugin templates are available yet.');
      return;
    }
    if (!isTemplateAvailable(chosen)) {
      setMessage(`${chosen.name} is listed in the plugin catalog, but its runtime editor is not available yet.`);
      return;
    }
    const templateId = chosen?.id || null;
    setMessage('');
    if (isKaraokeTemplate(chosen)) {
      const config = buildDefaultKaraokeConfig();
      if (karaokeDashboardMode && selectedDashboardDeviceId) {
        config.performer_screen_device_id = selectedDashboardDeviceId;
      }
      setEditing({
        id: null,
        event_template_id: templateId,
        name: 'Karaoke Night',
        target_time: '',
        post_duration: 600,
        teaser_enabled: false,
        suppress_ads_teaser: false,
        suppress_ads_live: false,
        reduced_flashing: true,
        config,
        configText: '',
      });
    } else if (isJukeboxTemplate(chosen)) {
      const config = buildDefaultJukeboxConfig();
      if (jukeboxDashboardMode && selectedDashboardDeviceId) {
        config.playback_target_device_id = selectedDashboardDeviceId;
      }
      setEditing({
        id: null,
        event_template_id: templateId,
        name: 'Jukebox',
        target_time: '',
        post_duration: 600,
        teaser_enabled: false,
        suppress_ads_teaser: false,
        suppress_ads_live: false,
        reduced_flashing: true,
        config,
        configText: '',
      });
    } else {
      const target = buildNyeTarget();
      setEditing({
        id: null,
        event_template_id: templateId,
        name: '',
        target_time: fmtDateTimeLocal(target),
        post_duration: 600,
        teaser_enabled: true,
        suppress_ads_teaser: false,
        suppress_ads_live: true,
        reduced_flashing: true,
        config: {
          title: 'Countdown',
          subtitle: '',
          theme: 'Classic',
          revelation_message: '',
          background_mode: 'blank',
          overlay_transparency: 60,
          photo_duration_sec: 45,
          photo_asset_ids: [],
        },
        configText: '',
      });
    }
    setModalOpen(true);
    setPreviewTick((v) => v + 1);
  };

  const openEdit = (inst) => {
    const cfg = inst?.config && typeof inst.config === 'object' ? inst.config : {};
    const template = templateById.get(Number(inst?.event_template_id || 0)) || null;
    setMessage('');
    setEditing({
      id: inst.id,
      event_template_id: inst.event_template_id,
      name: inst.name || '',
      target_time: inst.target_time ? fmtDateTimeLocal(inst.target_time) : '',
      post_duration: Number(inst.post_duration) || 600,
      teaser_enabled: !!inst.teaser_enabled,
      suppress_ads_teaser: !!inst.suppress_ads_teaser,
      suppress_ads_live: !!inst.suppress_ads_live,
      reduced_flashing: !!inst.reduced_flashing,
      config: isKaraokeTemplate(template)
        ? normalizeKaraokeConfig(cfg)
        : isJukeboxTemplate(template)
          ? normalizeJukeboxConfig(cfg)
          : {
            title: cfg.title || '',
            subtitle: cfg.subtitle || '',
            theme: cfg.theme || 'Classic',
            revelation_message: cfg.revelation_message || '',
            background_mode: cfg.background_mode || 'blank',
            overlay_transparency:
              Number.isFinite(Number(cfg.overlay_transparency)) ? Number(cfg.overlay_transparency) : 60,
            photo_duration_sec: Number.isFinite(Number(cfg.photo_duration_sec)) ? Number(cfg.photo_duration_sec) : 45,
            photo_asset_ids: Array.isArray(cfg.photo_asset_ids)
              ? cfg.photo_asset_ids.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
              : [],
          },
      configText: JSON.stringify(cfg || {}, null, 2),
    });
    setModalOpen(true);
    setPreviewTick((v) => v + 1);
  };

  const setSongQueuePlaybackTarget = (field, deviceId) => {
    const normalizedDeviceId = Number(deviceId);
    const nextDeviceId = Number.isInteger(normalizedDeviceId) && normalizedDeviceId > 0
      ? normalizedDeviceId
      : null;

    setEditing((current) => {
      if (!current) return current;
      const nextQrOverlayTargets = normalizeQrOverlayTargets(
        current.config?.qr_overlay_targets,
        current.config?.promotion_screen_device_ids
      );
      const nextAreaId = songQueueAreaIdByDeviceId.get(nextDeviceId) || null;
      const sameAreaQrOverlayTargets = nextAreaId
        ? nextQrOverlayTargets.filter((target) => (
          songQueueAreaIdByDeviceId.get(Number(target.device_id)) === nextAreaId
        ))
        : nextQrOverlayTargets;

      return {
        ...current,
        config: {
          ...current.config,
          [field]: nextDeviceId,
          end_condition: {
            ...current.config.end_condition,
            time_zone: songQueueTimeZoneByDeviceId.get(nextDeviceId)
              || current.config.end_condition?.time_zone
              || 'UTC',
          },
          qr_overlay_targets: sameAreaQrOverlayTargets,
          promotion_screen_device_ids: sameAreaQrOverlayTargets
            .filter((target) => String(target.position || '').startsWith('full'))
            .map((target) => target.device_id),
        },
      };
    });
  };

  const updateQrOverlayTarget = (deviceId, nextTarget) => {
    const normalizedDeviceId = Number(deviceId);
    if (!Number.isInteger(normalizedDeviceId) || normalizedDeviceId <= 0) return;

    setEditing((current) => {
      if (!current) return current;
      const playbackTargetDeviceId = Number(
        current.config?.performer_screen_device_id || current.config?.playback_target_device_id || 0
      );
      const isSamePlaybackTarget = playbackTargetDeviceId === normalizedDeviceId;
      const isJukeboxConfig = String(current.config?.plugin_key || '').toLowerCase() === 'jukebox';
      if (
        isSamePlaybackTarget
        && !isJukeboxConfig
        && nextTarget?.position
        && !['top', 'bottom'].includes(String(nextTarget.position).toLowerCase())
      ) return current;

      const currentTargets = normalizeQrOverlayTargets(
        current.config?.qr_overlay_targets,
        current.config?.promotion_screen_device_ids
      );
      const withoutDevice = currentTargets.filter((target) => target.device_id !== normalizedDeviceId);
      const withoutExistingQueueActivityTarget = nextTarget?.position === 'full_on_queue_addition'
        ? withoutDevice.filter((target) => target.position !== 'full_on_queue_addition')
        : withoutDevice;
      const nextTargets = nextTarget?.position
        ? [...withoutExistingQueueActivityTarget, {
            device_id: normalizedDeviceId,
            position: String(nextTarget.position || '').trim().toLowerCase(),
            duration_sec: Number.isFinite(Number(nextTarget.duration_sec)) && Number(nextTarget.duration_sec) > 0
              ? Number(nextTarget.duration_sec)
              : null,
            text_size_px: Math.min(64, Math.max(12, Number(nextTarget.text_size_px) || 24)),
            qr_size_px: Math.min(320, Math.max(64, Number(nextTarget.qr_size_px) || 140)),
            width_percent: Math.min(100, Math.max(40, Number(nextTarget.width_percent) || 80)),
            transparency: Math.min(95, Math.max(
              0,
              Number.isFinite(Number(nextTarget.transparency)) ? Number(nextTarget.transparency) : 80
            )),
          }]
        : withoutExistingQueueActivityTarget;

      return {
        ...current,
        config: {
          ...current.config,
          qr_overlay_targets: nextTargets,
          promotion_screen_device_ids: nextTargets
            .filter((target) => String(target.position || '').startsWith('full'))
            .map((target) => target.device_id),
        },
      };
    });
  };

  const setQrOverlayTargetStyle = (deviceId, field, value) => {
    const normalizedDeviceId = Number(deviceId);
    if (!Number.isInteger(normalizedDeviceId) || normalizedDeviceId <= 0) return;
    const limits = {
      text_size_px: [12, 64],
      qr_size_px: [64, 320],
      width_percent: [40, 100],
      transparency: [0, 95],
    };
    const range = limits[field];
    if (!range) return;
    const parsedValue = Number(value);
    if (!Number.isFinite(parsedValue)) return;
    const normalizedValue = Math.min(range[1], Math.max(range[0], parsedValue));

    setEditing((current) => {
      if (!current) return current;
      const nextTargets = normalizeQrOverlayTargets(
        current.config?.qr_overlay_targets,
        current.config?.promotion_screen_device_ids
      ).map((target) => Number(target.device_id) === normalizedDeviceId
        ? { ...target, [field]: normalizedValue }
        : target);
      return {
        ...current,
        config: {
          ...current.config,
          qr_overlay_targets: nextTargets,
        },
      };
    });
  };

  const setQrOverlayTargetPosition = (deviceId, position) => {
    const normalizedPosition = String(position || '').trim().toLowerCase();
    if (!normalizedPosition) {
      updateQrOverlayTarget(deviceId, null);
      return;
    }
    if (normalizedPosition === 'top' || normalizedPosition === 'bottom') {
      updateQrOverlayTarget(deviceId, { position: normalizedPosition });
      return;
    }
    if (normalizedPosition === 'full_permanent') {
      updateQrOverlayTarget(deviceId, { position: normalizedPosition });
      return;
    }
    if (normalizedPosition === 'full_on_queue_activity') {
      updateQrOverlayTarget(deviceId, { position: 'full_on_queue_addition', duration_sec: 30 });
    }
  };

  const setQrOverlayTargetDuration = (deviceId, durationSec) => {
    const normalizedDeviceId = Number(deviceId);
    const nextDurationSec = Number(durationSec);
    if (!Number.isInteger(normalizedDeviceId) || normalizedDeviceId <= 0) return;
    if (!Number.isFinite(nextDurationSec) || nextDurationSec <= 0) return;

    setEditing((current) => {
      if (!current) return current;
      const currentTargets = normalizeQrOverlayTargets(
        current.config?.qr_overlay_targets,
        current.config?.promotion_screen_device_ids
      );
      const nextTargets = currentTargets.map((target) => {
        if (Number(target.device_id) !== normalizedDeviceId) return target;
        if (String(target.position || '').toLowerCase() !== 'full_on_queue_addition') return target;
        return {
          ...target,
          duration_sec: nextDurationSec,
        };
      });

      return {
        ...current,
        config: {
          ...current.config,
          qr_overlay_targets: nextTargets,
          promotion_screen_device_ids: nextTargets
            .filter((target) => String(target.position || '').startsWith('full'))
            .map((target) => target.device_id),
        },
      };
    });
  };

  useEffect(() => {
    if (!openEventInstanceId) return;
    const match = instances.find((i) => Number(i.id) === Number(openEventInstanceId)) || null;
    if (!match) return;
    openEdit(match);
    onConsumeOpenRequest?.();
  }, [openEventInstanceId, instances]);

  const postPreview = useCallback(() => {
    const iframe = document.getElementById('events-preview-frame');
    if (!iframe || !editing) return;
    const target = editing.target_time ? parseDateTimeLocal(editing.target_time) : null;
    let config = { ...(editing.config || {}) };
    if (editing.configText && editing.configText.trim()) {
      try {
        config = JSON.parse(editing.configText);
      } catch (err) {}
    }
    if (target) config.target_time = target.toISOString();
    config.post_duration = Number(editing.post_duration) || 600;
    config.reduced_flashing = !!editing.reduced_flashing;

    try {
      iframe.contentWindow.postMessage(
        { type: 'showEvent', payload: { eventId: editing.id || null, mode: 'live', config } },
        '*'
      );
    } catch (err) {}
  }, [editing]);

  useEffect(() => {
    if (!modalOpen || !editing) return;
    const timer = setTimeout(() => postPreview(), 150);
    return () => clearTimeout(timer);
  }, [modalOpen, editing, previewTick, postPreview]);

  const saveEditing = async () => {
    if (!editing) return;
    if (stageLocked) {
      showNudge({
        title: 'Upgrade to unlock Plugins',
        message: 'Plugins are in demo mode on your current plan. Upgrade to save plugin instances.',
      });
      return;
    }
    try {
      setMessage('');
      const target = editing.target_time ? parseDateTimeLocal(editing.target_time) : null;
      const supportsAdvancedConfig = editingTemplateIsCountdown;
      let advancedConfig = null;
      if (supportsAdvancedConfig && editing.configText && editing.configText.trim()) {
        advancedConfig = JSON.parse(editing.configText);
      }
      const config = editingTemplateIsKaraoke
        ? normalizeKaraokeConfig(editing.config)
        : editingTemplateIsJukebox
          ? normalizeJukeboxConfig(editing.config)
          : advancedConfig || {
              ...editing.config,
            };
      if (editingTemplateIsKaraoke && !config.performer_screen_device_id) {
        throw new Error('Select a performer screen in the Karaoke setup before saving.');
      }
      if (editingTemplateIsJukebox && !config.playback_target_device_id) {
        throw new Error('Select a playback target in the Jukebox setup before saving.');
      }
      if (editingTemplateIsKaraoke || editingTemplateIsJukebox) {
        const primaryDeviceId = Number(
          editingTemplateIsJukebox
            ? config.playback_target_device_id
            : config.performer_screen_device_id
        );
        const primaryAreaId = songQueueAreaIdByDeviceId.get(primaryDeviceId) || null;
        if (!primaryAreaId) {
          throw new Error('The primary playback screen must belong to a known area.');
        }
        const participatingDeviceIds = normalizePositiveIntegerList([
          ...(config.mirror_playback_device_ids || []),
          ...(config.promotion_screen_device_ids || []),
          ...normalizeQrOverlayTargets(
            config.qr_overlay_targets,
            config.promotion_screen_device_ids
          ).map((overlayTarget) => overlayTarget.device_id),
        ]);
        const crossAreaDevice = participatingDeviceIds.find((participatingDeviceId) => (
          songQueueAreaIdByDeviceId.get(participatingDeviceId) !== primaryAreaId
        ));
        if (crossAreaDevice) {
          throw new Error('All playback and promotion screens must belong to the primary screen area.');
        }
      }
      if (target) config.target_time = target.toISOString();
      config.reduced_flashing = !!editing.reduced_flashing;

      const payload = {
        site_id: siteId,
        event_template_id: editing.event_template_id,
        name: editing.name,
        config,
        target_time: target ? target.toISOString() : null,
        post_duration: Number(editing.post_duration) || 600,
        teaser_enabled: !!editing.teaser_enabled,
        suppress_ads_teaser: !!editing.suppress_ads_teaser,
        suppress_ads_live: !!editing.suppress_ads_live,
        reduced_flashing: !!editing.reduced_flashing,
      };

      if (!payload.event_template_id) throw new Error('Missing template (event_template_id)');
      if (!payload.name || !payload.name.trim()) throw new Error('Name is required');
      if (!editingTemplateIsCountdown && !editingTemplateIsKaraoke && !editingTemplateIsJukebox) {
        throw new Error('This plugin editor is not available yet.');
      }

      if (editing.id) {
        await schedulerFetch(`/api/event-instances/${editing.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await schedulerFetch('/api/event-instances', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      setModalOpen(false);
      setEditing(null);
      await fetchInstances();
      setMessage('Saved.');
    } catch (err) {
      console.error('[Events] Save failed', err);
      setMessage(err.message || 'Save failed');
    }
  };

  const getSongQueueDefinitionForInstance = useCallback((inst) => {
    const template = templateById.get(Number(inst?.event_template_id || 0)) || null;
    if (isJukeboxTemplate(template)) {
      return {
        key: 'jukebox',
        label: 'Jukebox',
        apiBase: '/jukebox',
        normalizeConfig: normalizeJukeboxConfig,
        getGuestPath: getJukeboxGuestPath,
      };
    }
    if (isKaraokeTemplate(template)) {
      return {
        key: 'karaoke',
        label: 'Karaoke',
        apiBase: '/karaoke',
        normalizeConfig: normalizeKaraokeConfig,
        getGuestPath: getKaraokeGuestPath,
      };
    }
    return activeSongQueueDefinition;
  }, [activeSongQueueDefinition, templateById]);

  const launchSongQueueSession = async (inst) => {
    if (!inst?.id) return;
    const definition = getSongQueueDefinitionForInstance(inst);
    if (!definition) return;
    try {
      setMessage('');
      const result = await appFetch(`${definition.apiBase}/event-instances/${inst.id}/launch`, {
        method: 'POST',
      });
      const guestPath = result?.guestPath || definition.getGuestPath(result?.eventInstance?.config || inst?.config);
      await fetchInstances();
      setRuntimeData(result);
      setRuntimeInstanceId(inst.id);
      if (dedicatedSongQueueDashboardMode) setRuntimeModalOpen(false);
      setMessage(
        guestPath
          ? `${definition.label} session ready: ${window.location.origin}${guestPath}`
          : `${definition.label} session launched.`
      );
    } catch (err) {
      console.error(`[Events] ${definition.label} launch failed`, err);
      setMessage(err.message || `Failed to launch ${definition.label.toLowerCase()} session`);
    }
  };

  const stopSongQueueSession = async (inst) => {
    if (!inst?.id) return;
    const definition = getSongQueueDefinitionForInstance(inst);
    if (!definition) return;
    try {
      setMessage('');
      const result = await appFetch(`${definition.apiBase}/event-instances/${inst.id}/stop`, {
        method: 'POST',
      });
      await fetchInstances();
      if (dedicatedSongQueueDashboardMode) {
        setRuntimeData(null);
        setRuntimeInstanceId(null);
      } else {
        setRuntimeData(result);
        setRuntimeInstanceId(inst.id);
      }
      setMessage(`${definition.label} stopped. Playback and QR overlay screens have been released.`);
    } catch (err) {
      console.error(`[Events] ${definition.label} stop failed`, err);
      setMessage(err.message || `Failed to stop ${definition.label.toLowerCase()} session`);
    }
  };

  const resetSongQueueSession = async (inst) => {
    if (!inst?.id) return;
    const definition = getSongQueueDefinitionForInstance(inst);
    if (!definition) return;
    try {
      setMessage('');
      setRuntimeActing(true);
      const result = await appFetch(`${definition.apiBase}/event-instances/${inst.id}/reset`, {
        method: 'POST',
      });
      const guestPath = result?.guestPath || definition.getGuestPath(result?.eventInstance?.config || inst?.config);
      await fetchInstances();
      setRuntimeData(result);
      setRuntimeInstanceId(inst.id);
      setMessage(
        guestPath
          ? `${definition.label} reset complete. Fresh guest link: ${window.location.origin}${guestPath}`
          : `${definition.label} reset complete.`
      );
    } catch (err) {
      console.error(`[Events] ${definition.label} reset failed`, err);
      setMessage(err.message || `Failed to reset ${definition.label.toLowerCase()} session`);
    } finally {
      setRuntimeActing(false);
    }
  };

  const loadSongQueueRuntime = useCallback(async (eventInstanceId, options = {}) => {
    if (!eventInstanceId) return;
    const silent = options.silent === true;
    const inst = instances.find((item) => Number(item.id) === Number(eventInstanceId)) || runtimeData?.eventInstance || null;
    const definition = getSongQueueDefinitionForInstance(inst) || activeSongQueueDefinition;
    if (!definition) return;
    try {
      if (!silent) setRuntimeLoading(true);
      const result = await appFetch(`${definition.apiBase}/event-instances/${eventInstanceId}/runtime`);
      setRuntimeData(result);
      setRuntimeInstanceId(eventInstanceId);
    } catch (err) {
      console.error(`[Events] Failed to load ${definition.label} runtime`, err);
      if (!silent) setMessage(err.message || `Failed to load ${definition.label.toLowerCase()} runtime`);
    } finally {
      if (!silent) setRuntimeLoading(false);
    }
  }, [activeSongQueueDefinition, getSongQueueDefinitionForInstance, instances, runtimeData?.eventInstance]);

  useEffect(() => {
    if ((!runtimeModalOpen && !dedicatedSongQueueDashboardMode) || !runtimeInstanceId || runtimeActing) return undefined;

    const interval = window.setInterval(() => {
      loadSongQueueRuntime(runtimeInstanceId, { silent: true });
    }, 3500);

    return () => window.clearInterval(interval);
  }, [dedicatedSongQueueDashboardMode, loadSongQueueRuntime, runtimeActing, runtimeInstanceId, runtimeModalOpen]);

  const openSongQueueRuntime = async (inst) => {
    if (!inst?.id) return;
    setRuntimeModalOpen(true);
    await loadSongQueueRuntime(inst.id);
  };

  const runSongQueueRuntimeAction = async (action, queueItemId = null, extraPayload = {}) => {
    if (!runtimeInstanceId) return;
    const inst = instances.find((item) => Number(item.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
    const definition = getSongQueueDefinitionForInstance(inst) || activeSongQueueDefinition;
    if (!definition) return;
    try {
      setRuntimeActing(true);
      const result = await appFetch(`${definition.apiBase}/event-instances/${runtimeInstanceId}/runtime-actions`, {
        method: 'POST',
        body: JSON.stringify({
          ...extraPayload,
          action,
          queueItemId,
        }),
      });
      setRuntimeData(result);
      await fetchInstances();
    } catch (err) {
      console.error(`[Events] ${definition.label} runtime action failed`, err);
      setMessage(err.message || `Failed to update ${definition.label.toLowerCase()} runtime`);
    } finally {
      setRuntimeActing(false);
    }
  };

  const searchSongQueueRuntimeCatalog = async (query) => {
    if (!runtimeInstanceId) return { items: [] };
    const inst = instances.find((item) => Number(item.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
    const definition = getSongQueueDefinitionForInstance(inst) || activeSongQueueDefinition;
    if (!definition) return { items: [] };
    return appFetch(`${definition.apiBase}/event-instances/${runtimeInstanceId}/search?q=${encodeURIComponent(query)}`);
  };

  const forceQueueSongQueueItem = async (item) => {
    if (!runtimeInstanceId) return null;
    const inst = instances.find((entry) => Number(entry.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
    const definition = getSongQueueDefinitionForInstance(inst) || activeSongQueueDefinition;
    if (!definition) return null;
    try {
      setRuntimeActing(true);
      const result = await appFetch(`${definition.apiBase}/event-instances/${runtimeInstanceId}/force-queue`, {
        method: 'POST',
        body: JSON.stringify({ item }),
      });
      setRuntimeData(result);
      await fetchInstances();
      return result;
    } catch (err) {
      console.error(`[Events] ${definition.label} force queue failed`, err);
      setMessage(err.message || `Failed to force song into ${definition.label.toLowerCase()} queue`);
      throw err;
    } finally {
      setRuntimeActing(false);
    }
  };

  const copySongQueueLink = async (inst) => {
    const definition = getSongQueueDefinitionForInstance(inst);
    const guestPath = definition?.getGuestPath(inst?.config);
    if (!guestPath) {
      setMessage(`Launch the ${definition?.label?.toLowerCase() || 'session'} first to generate a guest link.`);
      return;
    }
    const fullUrl = `${window.location.origin}${guestPath}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setMessage(`Copied guest link: ${fullUrl}`);
    } catch (err) {
      console.error('[Events] Clipboard copy failed', err);
      setMessage(fullUrl);
    }
  };

  const copyRuntimeGuestLink = async () => {
    const inst = instances.find((item) => Number(item.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
    const definition = getSongQueueDefinitionForInstance(inst) || activeSongQueueDefinition;
    const guestPath = runtimeData?.guestPath || definition?.getGuestPath(runtimeData?.eventInstance?.config);
    if (!guestPath) {
      setMessage(`Launch the ${definition?.label?.toLowerCase() || 'session'} first to generate a guest link.`);
      return;
    }
    const fullUrl = `${window.location.origin}${guestPath}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setMessage(`Copied guest link: ${fullUrl}`);
    } catch (err) {
      console.error('[Events] Clipboard copy failed', err);
      setMessage(fullUrl);
    }
  };

  const deleteInstance = async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this plugin instance?')) return;
    try {
      await schedulerFetch(`/api/event-instances/${id}`, { method: 'DELETE' });
      await fetchInstances();
    } catch (err) {
      console.error('[Events] Delete failed', err);
      setMessage(err.message || 'Delete failed');
    }
  };

  const ensureLiveScheduledForDevice = async (inst) => {
    if (!deviceId) {
      setMessage('Select a device (in the main UI) to auto-schedule the live plugin instance for that device.');
      return;
    }
    if (!inst?.target_time) throw new Error('Cannot auto-schedule without a target time');
    const target = new Date(inst.target_time);
    if (Number.isNaN(target.getTime())) throw new Error('Invalid target time');
    const postDurationSec = Number(inst.post_duration) || 600;
    const fixedEnd = new Date(target.getTime() + postDurationSec * 1000);
    const desiredStart = new Date(target.getTime() - 60 * 60 * 1000);

    const rangeStart = new Date(desiredStart.getTime() - 2 * 60 * 60 * 1000);
    const rangeEnd = new Date(fixedEnd.getTime() + 2 * 60 * 60 * 1000);
    const res = await schedulerFetch(
      `/api/scheduled-items?deviceId=${encodeURIComponent(deviceId)}&start=${encodeURIComponent(
        rangeStart.toISOString()
      )}&end=${encodeURIComponent(rangeEnd.toISOString())}`
    );
    const items = Array.isArray(res.items) ? res.items : [];
    const already = items.some(
      (row) =>
        row.item_type === 'event' &&
        (row.event_mode || 'live') === 'live' &&
        Number(row.item_id) === Number(inst.id)
    );
    if (already) return;

    await schedulerFetch('/api/scheduled-items', {
      method: 'POST',
      body: JSON.stringify({
        deviceIds: [deviceId],
        item_type: 'event',
        item_id: inst.id,
        event_mode: 'live',
        start_time: desiredStart.toISOString(),
        end_time: fixedEnd.toISOString(),
        timezone: 'UTC',
        recurrence_rule: '',
        randomize: false,
      }),
    });
  };

  const savedImageAssets = useMemo(
    () => savedAssets.filter((a) => String(a.content_type || '').toLowerCase() === 's3_image'),
    [savedAssets]
  );
  const filteredImageAssets = useMemo(() => {
    const q = savedAssetSearch.trim().toLowerCase();
    if (!q) return savedImageAssets;
    return savedImageAssets.filter((asset) => {
      const hay = `${asset.title || ''} ${asset.filename || ''} ${asset.id || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [savedImageAssets, savedAssetSearch]);

  const visibleInstances = useMemo(() => instances.filter((inst) => {
    const template = templateById.get(Number(inst.event_template_id)) || null;
    const karaokeInstance = isKaraokeTemplate(template);
    const jukeboxInstance = isJukeboxTemplate(template);
    if (karaokeDashboardMode) {
      if (!karaokeInstance) return false;
      const config = normalizeKaraokeConfig(inst.config);
      const instanceAreaId = songQueueAreaIdByDeviceId.get(Number(config.performer_screen_device_id));
      return selectedDashboardAreaId
        ? instanceAreaId === selectedDashboardAreaId
        : Number(config.performer_screen_device_id) === Number(selectedDashboardDeviceId);
    }
    if (jukeboxDashboardMode) {
      if (!jukeboxInstance) return false;
      const config = normalizeJukeboxConfig(inst.config);
      const instanceAreaId = songQueueAreaIdByDeviceId.get(Number(config.playback_target_device_id));
      return selectedDashboardAreaId
        ? instanceAreaId === selectedDashboardAreaId
        : Number(config.playback_target_device_id) === Number(selectedDashboardDeviceId);
    }
    return !karaokeInstance && !jukeboxInstance;
  }), [
    instances,
    jukeboxDashboardMode,
    karaokeDashboardMode,
    selectedDashboardAreaId,
    selectedDashboardDeviceId,
    songQueueAreaIdByDeviceId,
    templateById,
  ]);

  const activeSongQueueUsage = useMemo(() => instances.flatMap((inst) => {
    const template = templateById.get(Number(inst.event_template_id)) || null;
    const key = isJukeboxTemplate(template)
      ? 'jukebox'
      : isKaraokeTemplate(template)
        ? 'karaoke'
        : null;
    if (!key) return [];

    const config = key === 'jukebox'
      ? normalizeJukeboxConfig(inst.config)
      : normalizeKaraokeConfig(inst.config);
    if (String(config?.live_session?.status || '').toLowerCase() !== 'live') return [];

    return [{
      instanceId: Number(inst.id),
      key,
      label: key === 'jukebox' ? 'Jukebox' : 'Karaoke',
      name: inst.name || (key === 'jukebox' ? 'Jukebox' : 'Karaoke'),
      deviceIds: getSongQueueDeviceIds(template, inst.config),
      areaIds: [...new Set(
        getSongQueueDeviceIds(template, inst.config)
          .map((deviceId) => songQueueAreaIdByDeviceId.get(Number(deviceId)))
          .filter(Boolean)
      )],
    }];
  }), [instances, songQueueAreaIdByDeviceId, templateById]);

  const activeSongQueueUsageForSelectedDevice = useMemo(() => {
    if (!selectedDashboardDeviceId) return [];
    return activeSongQueueUsage.filter((usage) => selectedDashboardAreaId
      ? usage.areaIds.includes(selectedDashboardAreaId)
      : usage.deviceIds.some((usageDeviceId) => Number(usageDeviceId) === Number(selectedDashboardDeviceId))
    );
  }, [activeSongQueueUsage, selectedDashboardAreaId, selectedDashboardDeviceId]);

  const getOpposingSongQueueUsage = useCallback((inst) => {
    const definition = getSongQueueDefinitionForInstance(inst);
    if (!definition) return null;
    const template = templateById.get(Number(inst?.event_template_id || 0)) || null;
    const deviceIds = getSongQueueDeviceIds(template, inst?.config);
    const areaIds = [...new Set(
      deviceIds.map((deviceId) => songQueueAreaIdByDeviceId.get(Number(deviceId))).filter(Boolean)
    )];
    return activeSongQueueUsage.find((usage) =>
      usage.key !== definition.key
      && usage.instanceId !== Number(inst?.id || 0)
      && (areaIds.length
        ? usage.areaIds.some((areaId) => areaIds.includes(areaId))
        : usage.deviceIds.some((usageDeviceId) => deviceIds.includes(Number(usageDeviceId))))
    ) || null;
  }, [
    activeSongQueueUsage,
    getSongQueueDefinitionForInstance,
    songQueueAreaIdByDeviceId,
    templateById,
  ]);

  useEffect(() => {
    if (!dedicatedSongQueueDashboardMode || !runtimeInstanceId) return;
    const stillVisible = visibleInstances.some((inst) => Number(inst.id) === Number(runtimeInstanceId));
    if (stillVisible) return;
    setRuntimeData(null);
    setRuntimeInstanceId(null);
  }, [dedicatedSongQueueDashboardMode, runtimeInstanceId, visibleInstances]);

  const reservedSongQueueDeviceIds = useMemo(() => {
    if (!editingTemplateIsKaraoke && !editingTemplateIsJukebox) return new Set();
    const editingIsJukebox = editingTemplateIsJukebox;
    const reserved = new Set();
    instances.forEach((inst) => {
      if (editing?.id && Number(inst.id) === Number(editing.id)) return;
      const template = templateById.get(Number(inst.event_template_id)) || null;
      if (editingIsJukebox && !isKaraokeTemplate(template)) return;
      if (!editingIsJukebox && !isJukeboxTemplate(template)) return;
      const definition = isJukeboxTemplate(template)
        ? { normalizeConfig: normalizeJukeboxConfig }
        : isKaraokeTemplate(template)
          ? { normalizeConfig: normalizeKaraokeConfig }
          : null;
      const config = definition?.normalizeConfig(inst.config);
      if (String(config?.live_session?.status || '').toLowerCase() !== 'live') return;
      const deviceIds = getSongQueueDeviceIds(template, inst.config);
      const activeAreaIds = new Set(
        deviceIds.map((deviceId) => songQueueAreaIdByDeviceId.get(Number(deviceId))).filter(Boolean)
      );
      if (activeAreaIds.size) {
        karaokeDeviceTargets.forEach((target) => {
          if (activeAreaIds.has(Number(target.areaId))) reserved.add(Number(target.id));
        });
      } else {
        deviceIds.forEach((deviceId) => reserved.add(deviceId));
      }
    });
    return reserved;
  }, [
    editing?.id,
    editingTemplateIsJukebox,
    editingTemplateIsKaraoke,
    instances,
    karaokeDeviceTargets,
    songQueueAreaIdByDeviceId,
    templateById,
  ]);

  useEffect(() => {
    if (!dedicatedSongQueueDashboardMode || runtimeInstanceId || runtimeActing) return;
    const activeInstance = visibleInstances.find((inst) => {
      const definition = getSongQueueDefinitionForInstance(inst);
      const config = definition?.normalizeConfig(inst.config);
      return String(config?.live_session?.status || '').toLowerCase() === 'live';
    });
    if (activeInstance?.id) {
      loadSongQueueRuntime(activeInstance.id, { silent: true });
    }
  }, [
    dedicatedSongQueueDashboardMode,
    getSongQueueDefinitionForInstance,
    loadSongQueueRuntime,
    runtimeActing,
    runtimeInstanceId,
    visibleInstances,
  ]);

  const toggleEnabled = async (inst, next) => {
    try {
      await schedulerFetch(`/api/event-instances/${inst.id}`, {
        method: 'PUT',
        body: JSON.stringify({ site_id: siteId, is_enabled: !!next }),
      });
      await fetchInstances();
      if (next) {
        const updatedList = (
          await schedulerFetch(`/api/event-instances?siteId=${encodeURIComponent(siteId)}`)
        )?.items;
        const updated = (Array.isArray(updatedList) ? updatedList : []).find(
          (i) => Number(i.id) === Number(inst.id)
        );
        const template = templateById.get(Number((updated || inst).event_template_id));
        if (isCountdownTemplate(template)) {
          await ensureLiveScheduledForDevice(updated || inst);
        }
      }
    } catch (err) {
      console.error('[Events] Toggle failed', err);
      setMessage(err.message || 'Update failed');
    }
  };

  return (
    <div className="p-6 text-slate-100 bg-slate-950 min-h-[70vh]">
      <div className="max-w-5xl mx-auto grid gap-4">
        {!dedicatedSongQueueDashboardMode && (
        <SectionCard title="Current Plugin Model" icon={Clock}>
          <div className="grid gap-3 text-sm text-slate-300">
            <p>
              Plugin setup still uses the existing scheduler and <code className="text-slate-100">event_instances</code> model.
              Countdown Clock still uses the event renderer, while Karaoke now uses the same instance model plus a
              live-session launch step for guest participation.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="text-xs uppercase tracking-wide text-amber-200">Storage</div>
                <div className="mt-2 font-semibold text-white">Scheduler instances plus live sessions</div>
                <div className="mt-1 text-xs text-slate-400">Karaoke reuses event instances for setup, then launches a shareable live session with its own queue state.</div>
              </div>
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="text-xs uppercase tracking-wide text-emerald-200">Runnable Today</div>
                <div className="mt-2 font-semibold text-white">Countdown Clock and Karaoke</div>
                <div className="mt-1 text-xs text-slate-400">Countdown keeps preview and scheduling; Karaoke adds guest link launch and queue intake on the existing live-session foundation.</div>
              </div>
              <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="text-xs uppercase tracking-wide text-sky-200">Next Runtime Work</div>
                <div className="mt-2 font-semibold text-white">Playback and operator runtime</div>
                <div className="mt-1 text-xs text-slate-400">Next commits extend the console host flow, preloading, filler playback, and later voting / YouTube DJ reuse.</div>
              </div>
            </div>
          </div>
        </SectionCard>
        )}

        {!dedicatedSongQueueDashboardMode && (
        <LiveMusicLibraryPanel
          siteId={siteId}
          stageLocked={stageLocked}
          onLocked={() =>
            showNudge({
              title: 'Upgrade to unlock Plugins',
              message: 'Plugins are in demo mode on your current plan. Upgrade to manage live music song libraries and sets.',
            })
          }
        />
        )}

        {!dedicatedSongQueueDashboardMode && (
        <SectionCard title="Plugin Catalog" icon={Clock}>
          {templates.length ? (
            <div className="grid gap-3">
              <div className="text-xs text-slate-400">
                Countdown Clock is available here. Karaoke and Jukebox use their dedicated tabs when enabled.
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                      selectedCategory === cat ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                    }`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {templatesInCategory.map((tpl) => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => {
                      if (!isTemplateAvailable(tpl)) {
                        setMessage(`${tpl.name} is in the catalog, but its editor is not available yet.`);
                        return;
                      }
                      openNew(tpl);
                    }}
                    className={`text-left rounded-2xl p-4 grid gap-3 border border-white/5 ring-1 transition ${
                      isTemplateAvailable(tpl)
                        ? 'bg-white/5 ring-white/10 hover:bg-white/10'
                        : 'bg-white/[0.03] ring-amber-400/30 hover:bg-white/[0.06]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-white">{tpl.name}</div>
                      <span className={`px-2 py-1 rounded-lg text-[10px] uppercase tracking-wide font-semibold ${
                        isTemplateAvailable(tpl)
                          ? 'bg-emerald-500/20 text-emerald-100'
                          : 'bg-amber-500/20 text-amber-100'
                      }`}>
                        {isTemplateAvailable(tpl) ? 'Available Now' : 'Coming Soon'}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 leading-relaxed">{tpl.description || '—'}</div>
                    {getTemplateHighlights(tpl).length ? (
                      <div className="flex flex-wrap gap-2">
                        {getTemplateHighlights(tpl).map((item) => (
                          <span key={`${tpl.id}-${item}`} className="px-2 py-1 rounded-lg bg-white/5 text-[11px] text-slate-200">
                            {item}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    <div className={`text-[11px] mt-1 ${
                      isTemplateAvailable(tpl) ? 'text-indigo-200' : 'text-amber-200'
                    }`}>
                      {isTemplateAvailable(tpl) ? 'Create instance' : 'Runtime editor pending'}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-400">No templates found.</div>
          )}
        </SectionCard>
        )}

        {!dedicatedSongQueueDashboardMode && (
        <SectionCard title="Roadmap" icon={Clock}>
          <div className="text-xs text-slate-400 mb-2">
            Upcoming plugin bundles and experiences from the current implementation plan. These stay separate from the runnable Countdown Clock editor until the shared live-session foundation is added.
          </div>
          <div className="grid lg:grid-cols-[1.8fr,1fr] gap-4">
            <div className="grid gap-3">
              <div className="flex flex-wrap gap-2">
                {comingSoonCategories.map((cat) => (
                  <button
                    key={cat}
                    className={`px-3 py-1.5 rounded-xl ring-1 ring-white/10 text-xs ${
                      comingSoonCategory === cat ? 'bg-white/15' : 'bg-white/5 hover:bg-white/10'
                    }`}
                    onClick={() => setComingSoonCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {comingSoonItems.map((item) => (
                  <button
                    key={`${item.category}-${item.id}`}
                    type="button"
                    onClick={() => setSelectedComingSoon(item)}
                    className={`text-left rounded-2xl p-4 grid gap-2 transition border border-white/5 ring-1 ${
                      selectedComingSoon?.id === item.id
                        ? 'ring-amber-400/70 bg-white/10'
                        : 'ring-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide text-amber-200 font-semibold">
                      <span className="px-2 py-1 rounded-lg bg-amber-500/20 text-amber-100">Coming Soon</span>
                      <span className="text-slate-300">{item.category}</span>
                    </div>
                    <div className="text-sm font-semibold text-white">{item.title}</div>
                    <div className="text-xs text-slate-400 leading-relaxed max-h-16 overflow-hidden">
                      {item.description}
                    </div>
                    <div className="text-[11px] text-indigo-200">Preview details</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-2 min-h-[220px]">
              {selectedComingSoon ? (
                <>
                  <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-amber-200">
                    <span className="px-2 py-1 rounded-lg bg-amber-500/20 text-amber-100">Coming Soon</span>
                    <span className="text-slate-200">{selectedComingSoon.category}</span>
                  </div>
                  <div className="text-lg font-semibold text-white">{selectedComingSoon.title}</div>
                  <div className="text-sm text-slate-300 leading-relaxed whitespace-pre-line">
                    {selectedComingSoon.description}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    These entries are intentionally catalog-visible before their runtime implementation ships.
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-400">Select a roadmap item to learn more.</div>
              )}
            </div>
          </div>
        </SectionCard>
        )}

        <SectionCard
          title={karaokeDashboardMode ? 'Karaoke Events' : jukeboxDashboardMode ? 'Jukebox Events' : 'Plugin Instances'}
          icon={Clock}
          actions={
            <button
              onClick={() => openNew(karaokeDashboardMode ? karaokeTemplate : jukeboxDashboardMode ? jukeboxTemplate : null)}
              className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-xs ${
                stageLocked ? 'bg-slate-700/60 text-slate-300 cursor-not-allowed' : 'bg-indigo-600/90 hover:bg-indigo-600'
              }`}
              disabled={stageLocked || (dedicatedSongQueueDashboardMode && visibleInstances.length > 0)}
              title={dedicatedSongQueueDashboardMode && visibleInstances.length > 0
                ? `This area already has a saved ${karaokeDashboardMode ? 'Karaoke' : 'Jukebox'} instance.`
                : undefined}
            >
              <Plus className="w-4 h-4" /> {karaokeDashboardMode ? 'New Karaoke Event' : jukeboxDashboardMode ? 'New Jukebox Event' : 'New Plugin'}
            </button>
          }
        >
          {dedicatedSongQueueDashboardMode && activeSongQueueUsageForSelectedDevice.length ? (
            <div className="mb-3 flex flex-wrap gap-2">
              {activeSongQueueUsageForSelectedDevice.map((usage) => (
                <span
                  key={`${usage.key}-${usage.instanceId}`}
                  className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    usage.key === 'jukebox'
                      ? 'bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/25'
                      : 'bg-fuchsia-500/15 text-fuchsia-100 ring-1 ring-fuchsia-400/25'
                  }`}
                >
                  {usage.label} in use: {usage.name}
                </span>
              ))}
            </div>
          ) : null}
          {!siteId ? (
            <div className="text-sm text-slate-400">Select a site to manage {karaokeDashboardMode ? 'karaoke events' : jukeboxDashboardMode ? 'jukebox events' : 'plugins'}.</div>
          ) : loading ? (
            <div className="text-sm text-slate-400">Loading…</div>
          ) : visibleInstances.length ? (
            <div className="grid gap-2">
              {visibleInstances.map((inst) => {
                const template = templateById.get(Number(inst.event_template_id)) || null;
                const karaokeInstance = isKaraokeTemplate(template);
                const jukeboxInstance = isJukeboxTemplate(template);
                const songQueueInstance = karaokeInstance || jukeboxInstance;
                const songQueueDefinition = getSongQueueDefinitionForInstance(inst);
                const songQueueConfig = songQueueDefinition?.normalizeConfig(inst.config);
                const guestPath = songQueueDefinition?.getGuestPath(inst.config);
                const songQueueIsActive = String(songQueueConfig?.live_session?.status || '').toLowerCase() === 'live';
                const opposingSongQueueUsage = songQueueInstance && !songQueueIsActive
                  ? getOpposingSongQueueUsage(inst)
                  : null;
                return (
                  <div
                    key={inst.id}
                    className="flex items-center justify-between gap-3 px-3 py-3 rounded-xl bg-white/5 ring-1 ring-white/10"
                  >
                    <div className="min-w-0">
                      <div className="font-semibold text-sm text-white truncate">{inst.name}</div>
                      <div className="text-xs text-slate-400">
                        Template: {template?.name || 'Unknown'} •{' '}
                        {songQueueInstance ? (
                          <>
                            Approval: {songQueueConfig?.approval_required ? 'On' : 'Off'} • Voting:{' '}
                            {songQueueConfig?.allow_voting ? 'On' : 'Off'} •{' '}
                            {karaokeInstance ? (
                              <>
                                Performer:{' '}
                                {songQueueConfig?.performer_screen_device_id ? `#${songQueueConfig.performer_screen_device_id}` : 'Not set'} • Filler:{' '}
                                {songQueueConfig?.filler_mode === 'silence' ? 'Silence' : 'Ambient video'}
                              </>
                            ) : (
                              <>
                                Playback target:{' '}
                                {songQueueConfig?.playback_target_device_id ? `#${songQueueConfig.playback_target_device_id}` : 'Not set'}
                              </>
                            )}{' '}
                            • QR overlays: {songQueueConfig?.qr_overlay_targets?.length || 0}
                          </>
                        ) : (
                          <>
                            Target: {inst.target_time ? new Date(inst.target_time).toLocaleString() : '—'} • Outro:{' '}
                            {Math.round((Number(inst.post_duration) || 0) / 60)}m
                          </>
                        )}
                      </div>
                      {songQueueInstance ? (
                        <div className={`mt-1 inline-flex items-center rounded-full px-2 py-1 text-[11px] font-semibold ${
                          songQueueIsActive
                            ? 'bg-emerald-500/15 text-emerald-200'
                            : 'bg-slate-800/90 text-slate-300'
                        }`}>
                          {songQueueIsActive ? `${songQueueDefinition?.label || 'Session'} Active` : `${songQueueDefinition?.label || 'Session'} Stopped`}
                        </div>
                      ) : null}
                      {songQueueInstance && guestPath ? (
                        <div className="text-[11px] text-emerald-300 mt-1 truncate">
                          Guest link ready: {window.location.origin}{guestPath}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <label className="text-xs text-slate-200 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!inst.is_enabled}
                          onChange={(e) => toggleEnabled(inst, e.target.checked)}
                        />
                        Enabled
                      </label>
                      {songQueueInstance ? (
                        <>
                          {songQueueIsActive ? (
                            <button
                              className="text-[11px] px-3 py-2 rounded-xl bg-rose-600/85 hover:bg-rose-600 text-white"
                              onClick={() => stopSongQueueSession(inst)}
                            >
                              Stop {songQueueDefinition?.label || 'Session'}
                            </button>
                          ) : (
                            <button
                              className={`text-[11px] px-3 py-2 rounded-xl text-white ${
                                opposingSongQueueUsage
                                  ? 'bg-slate-700/70 text-slate-300 cursor-not-allowed'
                                  : 'bg-emerald-600/85 hover:bg-emerald-600'
                              }`}
                              onClick={() => launchSongQueueSession(inst)}
                              disabled={!!opposingSongQueueUsage}
                              title={
                                opposingSongQueueUsage
                                  ? `${opposingSongQueueUsage.label} is already running on one of this event's devices.`
                                  : undefined
                              }
                            >
                              {opposingSongQueueUsage
                                ? `${opposingSongQueueUsage.label} in use`
                                : `Start ${songQueueDefinition?.label || 'Session'}`}
                            </button>
                          )}
                          <button
                            className="text-[11px] px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20"
                            onClick={() => copySongQueueLink(inst)}
                          >
                            Copy Link
                          </button>
                          <button
                            className="text-[11px] px-3 py-2 rounded-xl bg-amber-600/80 hover:bg-amber-600 text-white"
                            onClick={() => resetSongQueueSession(inst)}
                          >
                            Reset
                          </button>
                          {!dedicatedSongQueueDashboardMode ? (
                            <button
                              className="text-[11px] px-3 py-2 rounded-xl bg-sky-600/70 hover:bg-sky-600"
                              onClick={() => openSongQueueRuntime(inst)}
                            >
                              Runtime
                            </button>
                          ) : null}
                        </>
                      ) : null}
                      <button
                        className="text-[11px] px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20"
                        onClick={() => openEdit(inst)}
                      >
                        Edit
                      </button>
                      <button
                        className="p-2 rounded-xl bg-white/10 hover:bg-rose-500/30"
                        onClick={() => deleteInstance(inst.id)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-400">
              {karaokeDashboardMode ? 'No karaoke events yet.' : jukeboxDashboardMode ? 'No jukebox events yet.' : 'No plugin instances yet.'}
            </div>
          )}
        </SectionCard>

        {dedicatedSongQueueDashboardMode && runtimeInstanceId ? (
          <SectionCard
            title={`${activeSongQueueDefinition?.label || 'Queue'} Runtime${runtimeData?.eventInstance?.name ? ` · ${runtimeData.eventInstance.name}` : ''}`}
            icon={Clock}
          >
            <KaraokeRuntimePanel
              runtime={runtimeData}
              loading={runtimeLoading}
              acting={runtimeActing}
              selectedDeviceId={null}
              onRefresh={() => loadSongQueueRuntime(runtimeInstanceId)}
              onCopyLink={copyRuntimeGuestLink}
              onReset={() => {
                const inst = instances.find((item) => Number(item.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
                if (inst) {
                  resetSongQueueSession(inst);
                }
              }}
              onAction={runSongQueueRuntimeAction}
              onSearchCatalog={searchSongQueueRuntimeCatalog}
              onForceQueueItem={forceQueueSongQueueItem}
            />
          </SectionCard>
        ) : null}

        {message ? (
          <div className="text-sm text-emerald-300 bg-emerald-900/30 border border-emerald-700/50 px-3 py-2 rounded">
            {message}
          </div>
        ) : null}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        title={editing?.id ? `Edit ${editingTemplate?.name || 'Plugin'} Instance` : `New ${editingTemplate?.name || 'Plugin'} Instance`}
        panelClassName={editingTemplateIsKaraoke || editingTemplateIsJukebox ? 'live-experience-editor-modal' : ''}
      >
        {editing ? (
          <div className={editingTemplateIsKaraoke || editingTemplateIsJukebox ? 'live-experience-editor' : 'grid md:grid-cols-2 gap-4'}>
            <div className={editingTemplateIsKaraoke || editingTemplateIsJukebox ? 'live-experience-editor__options' : 'grid gap-3'}>
              <label className="grid gap-1 text-sm">
                <span className="text-xs text-slate-300">Name</span>
                <input
                  className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                />
              </label>

              <label className="grid gap-1 text-sm">
                <span className="text-xs text-slate-300">Template</span>
                <select
                  className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                  value={editing.event_template_id || ''}
                  onChange={(e) => setEditing({ ...editing, event_template_id: Number(e.target.value) || null })}
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  {templates
                    .filter((template) => {
                      if (karaokeDashboardMode) return isKaraokeTemplate(template);
                      if (jukeboxDashboardMode) return isJukeboxTemplate(template);
                      return !isDedicatedSongQueueTemplate(template);
                    })
                    .map((t) => (
                    <option key={t.id} value={t.id} disabled={!isTemplateAvailable(t)}>
                      {t.name}
                    </option>
                  ))}
                </select>
                {!editingTemplateIsCountdown && !editingTemplateIsKaraoke && !editingTemplateIsJukebox ? (
                  <div className="text-xs text-amber-200">
                    This plugin catalog entry is visible now, but its dedicated runtime editor is still pending.
                  </div>
                ) : null}
              </label>

              {editingTemplateIsCountdown ? (
                <>
                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Target Time</span>
                    <input
                      type="datetime-local"
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={editing.target_time}
                      onChange={(e) => setEditing({ ...editing, target_time: e.target.value })}
                    />
                    <button
                      type="button"
                      className="text-xs text-indigo-200 underline w-fit"
                      onClick={() => setEditing({ ...editing, target_time: fmtDateTimeLocal(buildNyeTarget()) })}
                    >
                      Preset: New Year&apos;s Eve
                    </button>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        className="text-xs text-indigo-200 underline w-fit"
                        onClick={() => setEditing({ ...editing, target_time: fmtDateTimeLocal(new Date(Date.now() + 10 * 1000)) })}
                      >
                        Preview: +10s
                      </button>
                      <button
                        type="button"
                        className="text-xs text-indigo-200 underline w-fit"
                        onClick={() => setEditing({ ...editing, target_time: fmtDateTimeLocal(new Date(Date.now() - 1 * 1000)) })}
                      >
                        Preview: End Now
                      </button>
                    </div>
                  </label>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Outro Duration (minutes)</span>
                    <input
                      type="range"
                      min={1}
                      max={30}
                      step={1}
                      value={Math.round((Number(editing.post_duration) || 600) / 60)}
                      onChange={(e) => setEditing({ ...editing, post_duration: Number(e.target.value) * 60 })}
                    />
                    <div className="text-xs text-slate-400">
                      {Math.round((Number(editing.post_duration) || 600) / 60)} minutes
                    </div>
                  </label>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Title</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                      value={editing.config.title}
                      onChange={(e) => setEditing({ ...editing, config: { ...editing.config, title: e.target.value } })}
                    />
                  </label>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Subtitle</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                      value={editing.config.subtitle}
                      onChange={(e) =>
                        setEditing({ ...editing, config: { ...editing.config, subtitle: e.target.value } })
                      }
                    />
                  </label>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Revelation Message</span>
                    <input
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                      placeholder="Happy New Year Everyone!"
                      value={editing.config.revelation_message || ''}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          config: { ...editing.config, revelation_message: e.target.value },
                        })
                      }
                    />
                    <div className="text-xs text-slate-400">Shown at 0:00 during the outro.</div>
                  </label>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Theme</span>
                    <select
                      className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                      value={editing.config.theme}
                      onChange={(e) => setEditing({ ...editing, config: { ...editing.config, theme: e.target.value } })}
                    >
                      {['Classic', 'Neon', 'Minimal'].map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="grid gap-2">
                    <div className="text-xs text-slate-300">Background</div>
                    {[
                      { id: 'blank', label: 'Blank Screen' },
                      { id: 'video', label: 'Continue Video Playback' },
                      { id: 'slideshow', label: 'Photo Slideshow' },
                    ].map((opt) => (
                      <label key={opt.id} className="text-sm text-slate-200 flex items-center gap-2">
                        <input
                          type="radio"
                          name="event-background-mode"
                          checked={(editing.config.background_mode || 'blank') === opt.id}
                          onChange={() =>
                            setEditing({
                              ...editing,
                              config: { ...editing.config, background_mode: opt.id },
                            })
                          }
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>

                  <label className="grid gap-1 text-sm">
                    <span className="text-xs text-slate-300">Countdown Transparency</span>
                    <input
                      type="range"
                      min={0}
                      max={90}
                      step={1}
                      value={Math.max(0, Math.min(90, Number(editing.config.overlay_transparency) || 0))}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          config: { ...editing.config, overlay_transparency: Number(e.target.value) },
                        })
                      }
                    />
                    <div className="text-xs text-slate-400">
                      {Math.max(0, Math.min(90, Number(editing.config.overlay_transparency) || 0))}% transparent
                    </div>
                  </label>

                  {String(editing.config.background_mode || 'blank') === 'slideshow' ? (
                    <div className="grid gap-3 rounded-2xl bg-white/5 ring-1 ring-white/10 p-3">
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Photo Duration</span>
                        <input
                          type="range"
                          min={15}
                          max={180}
                          step={1}
                          value={Math.max(15, Math.min(180, Number(editing.config.photo_duration_sec) || 45))}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: { ...editing.config, photo_duration_sec: Number(e.target.value) },
                            })
                          }
                        />
                        <div className="text-xs text-slate-400">
                          {Math.max(15, Math.min(180, Number(editing.config.photo_duration_sec) || 45))} seconds
                        </div>
                      </label>

                      <div className="grid gap-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-xs text-slate-300">Slideshow Photos (S3 images)</div>
                          <div className="text-xs text-slate-400">
                            {(Array.isArray(editing.config.photo_asset_ids) ? editing.config.photo_asset_ids : []).length}{' '}
                            selected
                          </div>
                        </div>
                        <input
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500 text-sm"
                          placeholder="Search saved images…"
                          value={savedAssetSearch}
                          onChange={(e) => setSavedAssetSearch(e.target.value)}
                        />
                        <div className="max-h-[220px] overflow-auto rounded-xl ring-1 ring-white/10 bg-black/20 p-2">
                          {savedAssetsLoading ? (
                            <div className="text-xs text-slate-400 p-2">Loading saved assets…</div>
                          ) : filteredImageAssets.length ? (
                            <div className="grid gap-1">
                              {filteredImageAssets.map((asset) => {
                                const selected = (editing.config.photo_asset_ids || []).includes(asset.id);
                                const label = asset.title || asset.filename || `Image #${asset.id}`;
                                return (
                                  <label
                                    key={asset.id}
                                    className="flex items-center gap-2 text-sm text-slate-200 px-2 py-1 rounded-lg hover:bg-white/5"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={(e) => {
                                        const current = Array.isArray(editing.config.photo_asset_ids)
                                          ? editing.config.photo_asset_ids
                                          : [];
                                        const next = e.target.checked
                                          ? Array.from(new Set([...current, asset.id]))
                                          : current.filter((id) => id !== asset.id);
                                        setEditing({
                                          ...editing,
                                          config: { ...editing.config, photo_asset_ids: next },
                                        });
                                      }}
                                    />
                                    <span className="truncate">{label}</span>
                                  </label>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="text-xs text-slate-400 p-2">No saved images found.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="grid gap-2">
                    <label className="text-sm text-slate-200 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.reduced_flashing}
                        onChange={(e) => setEditing({ ...editing, reduced_flashing: e.target.checked })}
                      />
                      Reduced flashing
                    </label>
                    <label className="text-sm text-slate-200 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.teaser_enabled}
                        onChange={(e) => setEditing({ ...editing, teaser_enabled: e.target.checked })}
                      />
                      Teaser enabled
                    </label>
                    <label className="text-sm text-slate-200 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.suppress_ads_live}
                        onChange={(e) => setEditing({ ...editing, suppress_ads_live: e.target.checked })}
                      />
                      Suppress Full Ads
                    </label>
                    <label className="text-sm text-slate-200 flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!editing.suppress_ads_teaser}
                        onChange={(e) => setEditing({ ...editing, suppress_ads_teaser: e.target.checked })}
                      />
                      Suppress Top &amp; Bottom ads
                    </label>
                  </div>
                </>
              ) : editingTemplateIsKaraoke || editingTemplateIsJukebox ? (
                <>
                  <section className="live-experience-card live-experience-card--identity rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-4">
                    <div>
                      <div className="text-sm font-semibold text-white">Programme identity</div>
                      <div className="text-xs text-slate-400 mt-1">
                        Brand this venue programme without renaming its {editingTemplateIsJukebox ? 'Jukebox' : 'Karaoke'} engine. Blank fields use the normal experience copy.
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Public title</span>
                        <input
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={editing.config.programme_branding?.public_title || ''}
                          placeholder={editing.name || (editingTemplateIsJukebox ? 'Jukebox' : 'Karaoke Night')}
                          maxLength={120}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              programme_branding: {
                                ...editing.config.programme_branding,
                                public_title: e.target.value,
                              },
                            },
                          })}
                        />
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Public subtitle</span>
                        <input
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={editing.config.programme_branding?.public_subtitle || ''}
                          placeholder={editingTemplateIsJukebox ? 'You choose the videos' : 'Your stage, your song'}
                          maxLength={180}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              programme_branding: {
                                ...editing.config.programme_branding,
                                public_subtitle: e.target.value,
                              },
                            },
                          })}
                        />
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Guest instructions</span>
                        <textarea
                          className="min-h-[82px] rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={editing.config.programme_branding?.guest_instructions || ''}
                          placeholder="Choose a video from your phone and add it to the live queue."
                          maxLength={240}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              programme_branding: {
                                ...editing.config.programme_branding,
                                guest_instructions: e.target.value,
                              },
                            },
                          })}
                        />
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Promotional copy</span>
                        <textarea
                          className="min-h-[82px] rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={editing.config.programme_branding?.promotional_copy || ''}
                          placeholder="Pick the next crowd favourite."
                          maxLength={240}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              programme_branding: {
                                ...editing.config.programme_branding,
                                promotional_copy: e.target.value,
                              },
                            },
                          })}
                        />
                      </label>
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Theme</span>
                        <select
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                          value={editing.config.programme_branding?.theme_key || 'default'}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              programme_branding: {
                                ...editing.config.programme_branding,
                                theme_key: e.target.value,
                              },
                            },
                          })}
                        >
                          <option value="default">Suitestream</option>
                          <option value="neon">Neon night</option>
                          <option value="warm">Warm venue</option>
                          <option value="sports">Sports night</option>
                          <option value="cinema">Cinema night</option>
                        </select>
                      </label>
                    </div>
                    <div className="text-xs text-indigo-200">
                      Operator engine: {editingTemplateIsJukebox ? 'Jukebox' : 'Karaoke'}
                      {editing.config.programme_branding?.public_title
                        ? ` • Guests see “${editing.config.programme_branding.public_title}”`
                        : ''}
                    </div>
                  </section>

                  <section className="live-experience-card live-experience-card--timing rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">End condition</div>
                      <div className="text-xs text-slate-400 mt-1">
                        Permanent installations remain active until stopped. Timed launches persist their exact UTC end for restart recovery.
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">End</span>
                        <select
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                          value={editing.config.end_condition?.mode || 'until_stopped'}
                          onChange={(e) => setEditing({
                            ...editing,
                            config: {
                              ...editing.config,
                              end_condition: {
                                ...editing.config.end_condition,
                                mode: e.target.value,
                              },
                            },
                          })}
                        >
                          <option value="until_stopped">Until stopped</option>
                          <option value="duration">After a duration</option>
                          <option value="local_time">At a local time</option>
                          <option value="before_next_programme">Before the next scheduled programme</option>
                        </select>
                      </label>
                      {editing.config.end_condition?.mode === 'duration' ? (
                        <label className="grid gap-1 text-sm">
                          <span className="text-xs text-slate-300">Duration (minutes)</span>
                          <input
                            type="number"
                            min={1}
                            max={1440}
                            className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                            value={editing.config.end_condition?.duration_minutes || 15}
                            onChange={(e) => setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                end_condition: {
                                  ...editing.config.end_condition,
                                  duration_minutes: Math.min(1440, Math.max(1, Number(e.target.value) || 1)),
                                },
                              },
                            })}
                          />
                        </label>
                      ) : null}
                      {editing.config.end_condition?.mode === 'local_time' ? (
                        <>
                          <label className="grid gap-1 text-sm">
                            <span className="text-xs text-slate-300">Venue time</span>
                            <input
                              type="time"
                              className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                              value={editing.config.end_condition?.local_time || '23:00'}
                              onChange={(e) => setEditing({
                                ...editing,
                                config: {
                                  ...editing.config,
                                  end_condition: {
                                    ...editing.config.end_condition,
                                    local_time: e.target.value,
                                  },
                                },
                              })}
                            />
                          </label>
                          <label className="grid gap-1 text-sm">
                            <span className="text-xs text-slate-300">Time zone</span>
                            <input
                              className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                              value={editing.config.end_condition?.time_zone || 'UTC'}
                              placeholder="Pacific/Auckland"
                              onChange={(e) => setEditing({
                                ...editing,
                                config: {
                                  ...editing.config,
                                  end_condition: {
                                    ...editing.config.end_condition,
                                    time_zone: e.target.value,
                                  },
                                },
                              })}
                            />
                          </label>
                        </>
                      ) : null}
                    </div>
                    {editing.config.end_condition?.mode === 'before_next_programme' ? (
                      <div className="text-xs text-slate-400">
                        The next event, playlist, or app launch on the primary screen becomes the boundary. Recurring schedules are resolved in their saved time zone. If none exists, this instance stays active until stopped.
                      </div>
                    ) : null}
                  </section>

                  <section className="live-experience-card live-experience-card--queue rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                    <div>
                      <div className="text-sm font-semibold text-white">Queue Rules</div>
                      <div className="text-xs text-slate-400 mt-1">
                        {editingTemplateIsJukebox
                          ? 'Jukebox guests search YouTube with a forced music video suffix, then add their chosen video to the live queue.'
                          : 'Karaoke guests search YouTube with a forced karaoke suffix, then add their chosen video to the live queue.'}
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Search Suffix</span>
                        <input
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={
                            editingTemplateIsJukebox
                              ? editing.config.search_suffix ?? 'music video'
                              : editing.config.karaoke_search_suffix ?? 'karaoke'
                          }
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                [editingTemplateIsJukebox ? 'search_suffix' : 'karaoke_search_suffix']: e.target.value,
                              },
                            })
                          }
                        />
                      </label>

                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Search Result Cap</span>
                        <input
                          type="range"
                          min={3}
                          max={10}
                          step={1}
                          value={Math.min(10, Math.max(3, Number(editing.config.max_search_results) || 8))}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                max_search_results: Number(e.target.value),
                              },
                            })
                          }
                        />
                        <div className="text-xs text-slate-400">
                          {Math.min(10, Math.max(3, Number(editing.config.max_search_results) || 8))} results
                        </div>
                      </label>

                      {editingTemplateIsJukebox ? (
                        <>
                          <label className="grid gap-1 text-sm">
                            <span className="text-xs text-slate-300">Max Song Duration (minutes)</span>
                            <input
                              type="number"
                              min={0}
                              max={60}
                              className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                              value={Math.min(60, Math.max(0, Number(editing.config.max_song_duration_minutes) || 0))}
                              onChange={(e) =>
                                setEditing({
                                  ...editing,
                                  config: {
                                    ...editing.config,
                                    max_song_duration_minutes: Math.min(60, Math.max(0, Number(e.target.value) || 0)),
                                  },
                                })
                              }
                            />
                            <div className="text-xs text-slate-400">
                              0 means no duration limit. Longer results stay visible but cannot be requested.
                            </div>
                          </label>

                          <label className="flex items-start gap-2 text-sm rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={!!editing.config.youtube_short_video_filter}
                              onChange={(e) =>
                                setEditing({
                                  ...editing,
                                  config: {
                                    ...editing.config,
                                    youtube_short_video_filter: e.target.checked,
                                  },
                                })
                              }
                            />
                            <span>
                              <span className="block text-xs text-slate-300">Ask YouTube for short videos</span>
                              <span className="block text-xs text-slate-400">Adds YouTube&apos;s short-video search filter. Leave off for the current behavior.</span>
                            </span>
                          </label>
                        </>
                      ) : null}

                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Max Requests Per Guest</span>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={Math.min(
                            20,
                            Math.max(
                              1,
                              Number(editing.config.max_requests_per_guest)
                                || (editingTemplateIsJukebox
                                  ? JUKEBOX_DEFAULT_REQUESTS_PER_GUEST
                                  : KARAOKE_DEFAULT_REQUESTS_PER_GUEST)
                            )
                          )}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                max_requests_per_guest: Math.min(
                                  20,
                                  Math.max(
                                    1,
                                    Number(e.target.value)
                                      || (editingTemplateIsJukebox
                                        ? JUKEBOX_DEFAULT_REQUESTS_PER_GUEST
                                        : KARAOKE_DEFAULT_REQUESTS_PER_GUEST)
                                  )
                                ),
                              },
                            })
                          }
                        />
                      </label>

                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Max Votes Per Guest</span>
                        <input
                          type="number"
                          min={1}
                          max={100}
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={Math.min(
                            100,
                            Math.max(
                              1,
                              Number(editing.config.max_votes_per_guest)
                                || (editingTemplateIsJukebox
                                  ? JUKEBOX_DEFAULT_VOTES_PER_GUEST
                                  : KARAOKE_DEFAULT_VOTES_PER_GUEST)
                            )
                          )}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                max_votes_per_guest: Math.min(
                                  100,
                                  Math.max(
                                    1,
                                    Number(e.target.value)
                                      || (editingTemplateIsJukebox
                                        ? JUKEBOX_DEFAULT_VOTES_PER_GUEST
                                        : KARAOKE_DEFAULT_VOTES_PER_GUEST)
                                  )
                                ),
                              },
                            })
                          }
                        />
                      </label>

                      <label className="grid gap-1 text-sm">
                        <span className="text-xs text-slate-300">Guest Access Hours</span>
                        <input
                          type="number"
                          min={1}
                          max={24}
                          className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                          value={Math.min(
                            24,
                            Math.max(1, Number(editing.config.guest_access_hours) || DEFAULT_GUEST_ACCESS_HOURS)
                          )}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                guest_access_hours: Math.min(
                                  24,
                                  Math.max(1, Number(e.target.value) || DEFAULT_GUEST_ACCESS_HOURS)
                                ),
                              },
                            })
                          }
                        />
                        <div className="text-xs text-slate-400">
                          Guests must rejoin after this many hours. This only affects public guest request sessions.
                        </div>
                      </label>
                    </div>

                    {editingTemplateIsKaraoke ? (
                      <div className="grid gap-2">
                        <div className="text-xs text-slate-300">Between Singers</div>
                        {[
                          { id: 'ambient_video', label: 'Fade back to ambient video' },
                          { id: 'silence', label: 'Leave silence for announcements' },
                        ].map((opt) => (
                          <label key={opt.id} className="text-sm text-slate-200 flex items-center gap-2">
                            <input
                              type="radio"
                              name="karaoke-filler-mode"
                              checked={(editing.config.filler_mode || 'ambient_video') === opt.id}
                              onChange={() =>
                                setEditing({
                                  ...editing,
                                  config: { ...editing.config, filler_mode: opt.id },
                                })
                              }
                            />
                            {opt.label}
                          </label>
                        ))}
                      </div>
                    ) : null}

                    {editingTemplateIsJukebox || editingTemplateIsKaraoke ? (
                      <div className="grid gap-3 rounded-xl bg-black/20 ring-1 ring-white/10 p-3">
                        <label className="grid gap-1 text-sm">
                          <span className="text-xs text-slate-300">Add Choices to Playlist</span>
                          <select
                            className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                            value={editing.config.choice_playlist_mode || 'off'}
                            onChange={(e) =>
                              setEditing({
                                ...editing,
                                config: {
                                  ...editing.config,
                                  choice_playlist_mode: e.target.value,
                                  choice_playlist_name:
                                    e.target.value === 'new_playlist'
                                      ? editing.config.choice_playlist_name || `${editing.name || 'Jukebox'} Choices`
                                      : editing.config.choice_playlist_name || '',
                                },
                              })
                            }
                          >
                            <option value="off">Off</option>
                            {editingTemplateIsJukebox ? (
                              <option value="current_fallback">Save to current fallback playlist</option>
                            ) : null}
                            <option value="new_playlist">Add to a regular playlist</option>
                          </select>
                        </label>
                        {editing.config.choice_playlist_mode === 'new_playlist' ? (
                          <label className="grid gap-1 text-sm">
                            <span className="text-xs text-slate-300">Playlist Name</span>
                            <input
                              className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100 placeholder:text-slate-500"
                              value={editing.config.choice_playlist_name || ''}
                              placeholder={`${editing.name || 'Jukebox'} Choices`}
                              onChange={(e) =>
                                setEditing({
                                  ...editing,
                                  config: {
                                    ...editing.config,
                                    choice_playlist_name: e.target.value,
                                  },
                                })
                              }
                            />
                          </label>
                        ) : null}
                        {editingTemplateIsKaraoke ? (
                          <div className="text-xs text-slate-400">
                            Karaoke choices are saved to a regular playlist and never to a fallback playlist.
                          </div>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="grid gap-2">
                      <label className="text-sm text-slate-200 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!editing.config.approval_required}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                approval_required: e.target.checked,
                              },
                            })
                          }
                        />
                        Require host approval before a request can advance
                      </label>
                      <label className="text-sm text-slate-200 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!editing.config.allow_voting}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                allow_voting: e.target.checked,
                              },
                            })
                          }
                        />
                        Allow guests to vote queued songs up or down
                      </label>
                    </div>

                    <div className="live-experience-screen-roles grid gap-3 rounded-2xl bg-black/20 ring-1 ring-white/10 p-3">
                      <div>
                        <div className="text-xs text-slate-300">Screen Roles</div>
                        <div className="text-xs text-slate-500 mt-1">
                          {dedicatedSongQueueDashboardMode
                            ? `Choose any device in this area as the ${editingTemplateIsJukebox ? 'playback target' : 'performer screen'}. The saved setup remains available from every device in the area.`
                            : editingTemplateIsJukebox
                            ? 'Choose one primary playback screen for this area, then choose which other screens in the area should carry QR promotions.'
                            : 'Choose one performer screen for this area. It plays the karaoke video and can show a top or bottom QR overlay between songs. Other screens in the area can use full promotions.'}
                        </div>
                      </div>
                      {karaokeDeviceTargetsLoading ? (
                        <div className="text-xs text-slate-400">Loading site devices…</div>
                      ) : karaokeDeviceTargets.length ? (
                        <label className="grid gap-1 text-sm">
                          <span className="text-xs text-slate-300">
                            {editingTemplateIsJukebox ? 'Playback Target' : 'Performer Screen'}
                          </span>
                          <select
                            className="rounded-xl bg-white/5 ring-1 ring-white/10 px-3 py-2 text-slate-100"
                            value={Number(
                              editingTemplateIsJukebox
                                ? editing.config.playback_target_device_id
                                : editing.config.performer_screen_device_id
                            ) || ''}
                            onChange={(e) => {
                              if (editingTemplateIsJukebox) {
                                setSongQueuePlaybackTarget('playback_target_device_id', e.target.value);
                              } else {
                                setSongQueuePlaybackTarget('performer_screen_device_id', e.target.value);
                              }
                            }}
                          >
                            <option value="">
                              {editingTemplateIsJukebox ? 'Select playback target…' : 'Select performer screen…'}
                            </option>
                            {songQueuePlaybackTargets.map((target) => (
                              <option
                                key={`performer-${target.id}`}
                                value={target.id}
                              >
                                {formatKaraokeDeviceLabel(target)}
                                {reservedSongQueueDeviceIds.has(Number(target.id)) ? ' (currently in use)' : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                      ) : null}
                      <div>
                        <div className="text-xs text-slate-300">QR Overlay Screens</div>
                        <div className="text-xs text-slate-500 mt-1">
                          Only screens in the primary playback area participate. Top and Bottom use configurable QR overlays. On the Karaoke performer screen they appear only between songs. Full uses the promotion screen with queue and QR.
                        </div>
                      </div>
                      {karaokeDeviceTargetsLoading ? (
                        <div className="text-xs text-slate-400">Loading site devices…</div>
                      ) : songQueueAreaTargets.length ? (
                        <div className="grid gap-2 max-h-[360px] overflow-auto pr-1">
                          {songQueueAreaTargets.map((target) => (
                              <label
                                key={`audience-${target.id}`}
                                className="grid gap-2 rounded-xl bg-white/5 ring-1 ring-white/10 p-3 text-sm text-slate-200"
                              >
                                <span className="min-w-0 truncate">
                                  {target.name}
                                  <span className="text-xs text-slate-500"> • {target.areaName}</span>
                                </span>
                                <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-2 items-center">
                                  <select
                                    className="rounded-xl bg-black/20 ring-1 ring-white/10 px-2 py-1.5 text-xs text-slate-100"
                                    value={
                                      ((entry) => {
                                        const position = String(entry?.position || '');
                                        if (position === 'full_on_queue_addition') return 'full_on_queue_activity';
                                        if (position === 'full') return 'full_permanent';
                                        return position;
                                      })(
                                        normalizeQrOverlayTargets(
                                          editing.config.qr_overlay_targets,
                                          editing.config.promotion_screen_device_ids
                                        ).find((entry) => Number(entry.device_id) === Number(target.id))
                                      ) || ''
                                    }
                                    onChange={(e) => setQrOverlayTargetPosition(target.id, e.target.value)}
                                  >
                                    <option value="">Off</option>
                                    <option value="top">Top</option>
                                    <option value="bottom">Bottom</option>
                                    {editingTemplateIsJukebox
                                    || Number(target.id) !== Number(editing.config.performer_screen_device_id || 0) ? (
                                      <option value="full_permanent">Full Permanent</option>
                                    ) : null}
                                    {editingTemplateIsJukebox ? (
                                      <option value="full_on_queue_activity">Full On Queue Activity</option>
                                    ) : null}
                                  </select>
                                  {normalizeQrOverlayTargets(
                                    editing.config.qr_overlay_targets,
                                    editing.config.promotion_screen_device_ids
                                  ).find((entry) => Number(entry.device_id) === Number(target.id))?.position === 'full_on_queue_addition' ? (
                                    <div className="grid gap-1 text-[11px] text-slate-400">
                                      <span>Duration</span>
                                      <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        className="rounded-xl bg-black/20 ring-1 ring-white/10 px-2 py-1.5 text-xs text-slate-100"
                                        value={
                                          Number(
                                            normalizeQrOverlayTargets(
                                              editing.config.qr_overlay_targets,
                                              editing.config.promotion_screen_device_ids
                                            ).find((entry) => Number(entry.device_id) === Number(target.id))?.duration_sec
                                          ) || 30
                                        }
                                        onChange={(e) => setQrOverlayTargetDuration(target.id, e.target.value)}
                                      />
                                    </div>
                                  ) : null}
                                  {((overlayTarget) => (
                                    ['top', 'bottom'].includes(String(overlayTarget?.position || '').toLowerCase()) ? (
                                      <div className="col-span-2 grid grid-cols-2 lg:grid-cols-4 gap-2 rounded-lg bg-black/15 p-2">
                                        <label className="grid gap-1 text-[11px] text-slate-400">
                                          <span>Text px</span>
                                          <input
                                            type="number"
                                            min="12"
                                            max="64"
                                            className="rounded-lg bg-black/20 ring-1 ring-white/10 px-2 py-1 text-xs text-slate-100"
                                            value={Number(overlayTarget.text_size_px) || 24}
                                            onChange={(e) => setQrOverlayTargetStyle(target.id, 'text_size_px', e.target.value)}
                                          />
                                        </label>
                                        <label className="grid gap-1 text-[11px] text-slate-400">
                                          <span>QR px</span>
                                          <input
                                            type="number"
                                            min="64"
                                            max="320"
                                            className="rounded-lg bg-black/20 ring-1 ring-white/10 px-2 py-1 text-xs text-slate-100"
                                            value={Number(overlayTarget.qr_size_px) || 140}
                                            onChange={(e) => setQrOverlayTargetStyle(target.id, 'qr_size_px', e.target.value)}
                                          />
                                        </label>
                                        <label className="grid gap-1 text-[11px] text-slate-400">
                                          <span>Width %</span>
                                          <input
                                            type="number"
                                            min="40"
                                            max="100"
                                            className="rounded-lg bg-black/20 ring-1 ring-white/10 px-2 py-1 text-xs text-slate-100"
                                            value={Number(overlayTarget.width_percent) || 80}
                                            onChange={(e) => setQrOverlayTargetStyle(target.id, 'width_percent', e.target.value)}
                                          />
                                        </label>
                                        <label className="grid gap-1 text-[11px] text-slate-400">
                                          <span>Transparent %</span>
                                          <input
                                            type="number"
                                            min="0"
                                            max="95"
                                            className="rounded-lg bg-black/20 ring-1 ring-white/10 px-2 py-1 text-xs text-slate-100"
                                            value={Number(overlayTarget.transparency) || 0}
                                            onChange={(e) => setQrOverlayTargetStyle(target.id, 'transparency', e.target.value)}
                                          />
                                        </label>
                                      </div>
                                    ) : null
                                  ))(
                                    normalizeQrOverlayTargets(
                                      editing.config.qr_overlay_targets,
                                      editing.config.promotion_screen_device_ids
                                    ).find((entry) => Number(entry.device_id) === Number(target.id))
                                  )}
                                </div>
                                {reservedSongQueueDeviceIds.has(Number(target.id)) ? (
                                  <span className="text-[11px] text-amber-200">
                                    The other live queue plugin is currently running in this area. You can save changes, but must stop it before starting this instance.
                                  </span>
                                ) : null}
                              </label>
                          ))}
                        </div>
                      ) : (
                        <div className="text-xs text-slate-500">
                          {editingPrimaryPlaybackDeviceId
                            ? 'No compatible screens were found in this area.'
                            : 'Select the primary playback screen to choose participating screens.'}
                        </div>
                      )}
                      <label className="grid gap-2 text-sm">
                        <span className="text-xs text-slate-300">Promotion QR Size</span>
                        <input
                          type="range"
                          min="10"
                          max="80"
                          step="1"
                          value={Number(editing.config.promotion_qr_size) || 22}
                          onChange={(e) =>
                            setEditing({
                              ...editing,
                              config: {
                                ...editing.config,
                                promotion_qr_size: Number(e.target.value) || 22,
                              },
                            })
                          }
                        />
                        <span className="text-xs text-slate-500">
                          {Number(editing.config.promotion_qr_size) || 22}% of screen width, automatically capped to fit.
                        </span>
                      </label>
                    </div>
                  </section>
                </>
              ) : (
                <div className="rounded-2xl bg-amber-500/10 border border-amber-400/30 p-4 grid gap-2">
                  <div className="text-sm font-semibold text-amber-100">Dedicated editor pending</div>
                  <div className="text-xs text-amber-50/90 leading-relaxed">
                    {editingTemplate?.name || 'This plugin'} is now part of the visible catalog, but it does not use
                    the Countdown editor. Its setup and runtime tools will arrive after the shared live-session
                    foundation is implemented.
                  </div>
                </div>
              )}
            </div>

            <div className={editingTemplateIsKaraoke || editingTemplateIsJukebox ? 'live-experience-editor__aside' : 'grid gap-3'}>
              {editingTemplateIsCountdown ? (
                <>
                  <div className="text-xs text-slate-300">Live Preview</div>
                  <div className="rounded-2xl bg-black/40 ring-1 ring-white/10 overflow-hidden aspect-video">
                    <iframe
                      id="events-preview-frame"
                      title="Plugin Preview"
                      src="/events/event-renderer.html"
                      className="w-full h-full border-0"
                      onLoad={() => postPreview()}
                    />
                  </div>

                  <div className="text-xs text-slate-300">Advanced Config (JSON overrides simple fields)</div>
                  <textarea
                    className="min-h-[320px] rounded-xl bg-white/5 ring-1 ring-white/10 p-3 font-mono text-xs text-slate-100 placeholder:text-slate-500"
                    placeholder='{"title":"...","subtitle":"...","theme":"Classic"}'
                    value={editing.configText}
                    onChange={(e) => setEditing({ ...editing, configText: e.target.value })}
                  />
                </>
              ) : editingTemplateIsKaraoke || editingTemplateIsJukebox ? (
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                  <div className="text-xs uppercase tracking-wide text-emerald-200">Guest Link Workflow</div>
                  <div className="text-sm text-slate-200">
                    Save the instance, then launch the session from the instance list to generate a live guest link.
                  </div>
                  <div className="text-xs text-slate-400">
                    Guests will join at <code className="text-slate-100">/live/:publicKey</code>, search YouTube with the configured {editingTemplateIsJukebox ? 'music video' : 'karaoke'} suffix, and add requests into the shared queue.
                  </div>
                  {editing.config.live_session?.public_key ? (
                    <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/20 px-3 py-2 text-xs text-emerald-100 break-all">
                      Current link: {window.location.origin}{editingTemplateIsJukebox ? getJukeboxGuestPath(editing.config) : getKaraokeGuestPath(editing.config)}
                    </div>
                  ) : (
                    <div className="rounded-xl bg-slate-900/60 border border-white/10 px-3 py-2 text-xs text-slate-300">
                      No live guest link yet.
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 grid gap-3">
                  <div className="text-xs uppercase tracking-wide text-sky-200">Runtime Reserved</div>
                  <div className="text-sm text-slate-200">
                    This space will become the live operator preview and setup area for {editingTemplate?.name || 'this plugin'} once its runtime ships.
                  </div>
                  <div className="text-xs text-slate-400">
                    Countdown Clock continues to use the current event renderer. Interactive plugins will move here in later commits.
                  </div>
                </div>
              )}
              {!(editingTemplateIsKaraoke || editingTemplateIsJukebox) ? (
              <button
                onClick={saveEditing}
                className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm justify-center ${
                  editingTemplateIsCountdown || editingTemplateIsKaraoke || editingTemplateIsJukebox
                    ? 'bg-emerald-600/90 hover:bg-emerald-600'
                    : 'bg-slate-700/70 text-slate-300 cursor-not-allowed'
                }`}
                disabled={!editingTemplateIsCountdown && !editingTemplateIsKaraoke && !editingTemplateIsJukebox}
              >
                <Save className="w-4 h-4" /> {editingTemplateIsCountdown || editingTemplateIsKaraoke || editingTemplateIsJukebox ? 'Save' : 'Editor Pending'}
              </button>
              ) : null}
            </div>
            {editingTemplateIsKaraoke || editingTemplateIsJukebox ? (
              <div className="live-experience-editor__actions">
                <div>
                  <strong>{editingTemplateIsJukebox ? 'Jukebox' : 'Karaoke'} setup</strong>
                  <span>Review the player and promo screen roles, then save your changes.</span>
                </div>
                <button type="button" className="live-experience-editor__cancel" onClick={() => { setModalOpen(false); setEditing(null); }}>
                  Cancel
                </button>
                <button type="button" className="live-experience-editor__save" onClick={saveEditing}>
                  <Save className="w-4 h-4" /> Save instance
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>

      {!dedicatedSongQueueDashboardMode && (
      <Modal
        open={runtimeModalOpen}
        onClose={() => {
          setRuntimeModalOpen(false);
          setRuntimeInstanceId(null);
          setRuntimeData(null);
        }}
        title={`${activeSongQueueDefinition?.label || 'Queue'} Runtime${runtimeData?.eventInstance?.name ? ` · ${runtimeData.eventInstance.name}` : ''}`}
      >
        <KaraokeRuntimePanel
          runtime={runtimeData}
          loading={runtimeLoading}
          acting={runtimeActing}
          selectedDeviceId={null}
          onRefresh={() => loadSongQueueRuntime(runtimeInstanceId)}
          onCopyLink={copyRuntimeGuestLink}
          onReset={() => {
            const inst = instances.find((item) => Number(item.id) === Number(runtimeInstanceId)) || runtimeData?.eventInstance || null;
            if (inst) {
              resetSongQueueSession(inst);
            }
          }}
          onAction={runSongQueueRuntimeAction}
          onSearchCatalog={searchSongQueueRuntimeCatalog}
          onForceQueueItem={forceQueueSongQueueItem}
        />
      </Modal>
      )}
    </div>
  );
}
